import React, { useState, useEffect, useMemo, useCallback, useRef, startTransition } from 'react';
import { fetchRecipeList, getCachedRecipeList } from '../../utils/recipeListCache';
import {
  Stack,
  Text,
  Group,
  Button,
  Paper,
  Grid,
  Alert,
  Box,
  Skeleton,
  Card,
  ActionIcon,
  ThemeIcon,
  Transition,
  Tabs,
  Loader,
  SimpleGrid,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconRefresh,
  IconCheck,
  IconAlertCircle,
  IconChevronLeft,
  IconX,
  IconBulb,
  IconPlus,
} from '@tabler/icons-react';
import { MealPlan, GroceryItem, Recipe, MealPlanAssignment, CartAdditionStatus, MealType, getAssignmentMealType, CustomMeal, DeletedDaySummary, GroceryListGenerationProgress, PenciledMealMemory, SubstitutionGenerationProgress } from '../../types/mealPlan';
import { freshestProgress } from '../../utils/progressFreshness';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getAssignmentRecipe, getReferenceAnchor, substitutionKey, assignmentSubstitutionKey } from '../../utils/mealPlanHelpers';
import { selectedIngredientDisplays } from '../../utils/ingredientChips';
import { addFoodsToAvoid } from '../../utils/foodsToAvoid';
import { PantryItem } from '../../types/pantry';
import { FamilyProfile } from '../../types/familyProfile';
import { householdSegment } from '../../utils/householdSegment';
import { useMealPlanning } from '../../hooks/useMealPlanning';
import { useAppUser } from '../../hooks/useAppUser';
import { useMealPlanTour } from '../../hooks/useMealPlanTour';
import { useMealPlanActivatedTour } from '../../hooks/useMealPlanActivatedTour';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { AnalyticsEvent } from '../../utils/analytics';
import { CookScheduleView } from './CookScheduleView';
import { notifications } from '@mantine/notifications';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { addMealPlanToCart, getCartAdditionStatus, toggleGroceryListItems } from '../../services/krogerService';
import { syncMealPlanToCalendar, removeMealPlanFromCalendar } from '../../services/calendarService';
import { createInstacartShoppingList } from '../../services/instacartService';
import { MobileOptimized } from '../Layout/MobileOptimized';
import { KrogerLocationModal } from '../Kroger/KrogerLocationModal';
import { formatDateRange, isRecipeScaled, isMealPlanPast } from '../../utils/mealPlanUtils';
import { MealPlanPreferencesDrawer } from './MealPlanPreferencesDrawer';
import { RecipeCustomizationDrawer } from './RecipeCustomizationDrawer';
import { ShoppingCustomizationDrawer } from './ShoppingCustomizationDrawer';
import { RecipeSelectionDrawer } from './RecipeSelectionDrawer';
import { DeletedDayCard } from './DeletedDayCard';
import { CustomMealCard } from './CustomMealCard';
import { CustomMealDrawer } from './CustomMealDrawer';
import { ContextualFeedbackModal } from '../Support/ContextualFeedbackModal';
import { RecipeRatingModal } from './RecipeRatingModal';
import { MealPlanHeader } from './MealPlanHeader';
import { MealPlanSummary } from './MealPlanSummary';
import { printShoppingList } from '../../utils/printShoppingList';
import { NutritionDrawer } from '../Recipe/NutritionDrawer';
import { RecipeCard } from './RecipeCard';
import { SortableMealCard, SortableCustomMealCard, MealCardDragHandle } from './SortableMealCard';
import { KrogerCartResultsDrawer } from './KrogerCartResultsDrawer';
import { ShoppingList } from './ShoppingList';
import { ShoppingListDrawer } from './ShoppingListDrawer';
import { buildPlanPath, buildRecipePath } from '../../utils/routeHelpers';
import { shareLink } from '../../utils/shareLink';
import viewStyles from './MealPlanView.module.css';
import { collection, doc, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useFirestoreTrigger } from '../../hooks/useFirestoreTrigger';
import { getPageCache, setPageCache } from '../../utils/pageCache';
import { UserAvatar } from '../UI/UserAvatar';
import { ownerDisplayName } from '../../utils/ownerName';


// Soft-delete key: dinner keeps the bare date (pre-slot behavior); other
// slots are suffixed so deleting a breakfast can't hide the day's dinner.
const deletionKey = (assignment: MealPlanAssignment) => {
  const mealType = getAssignmentMealType(assignment);
  return mealType === 'dinner'
    ? assignment.plannedDate
    : `${assignment.plannedDate}#${mealType}`;
};

// The same key derived from a tombstone summary instead of a live assignment
const deletedDayKey = (day: Pick<DeletedDaySummary, 'plannedDate' | 'mealType'>) =>
  (day.mealType ?? 'dinner') === 'dinner'
    ? day.plannedDate
    : `${day.plannedDate}#${day.mealType}`;

// How long to keep a requested substitution in the loading state before giving
// up on ever seeing the backend mark it in flight. Only hit when the request
// succeeded but the assignment never got a substitutionStatus written.
const SUBSTITUTION_START_GRACE_MS = 15000;

type CartResultsState = {
  success: boolean;
  message: string;
  addedItems?: Array<{
    itemName: string;
    productName: string;
    quantity: number;
    reasoning?: string;
    productPageURI?: string;
  }>;
  failedItems?: Array<{
    itemName: string;
    reason: string;
  }>;
} | null;

type MealPlanCacheEntry = {
  mealPlan: MealPlan;
  owner: { uid: string; displayName?: string; photoURL?: string } | null;
  groceryList: GroceryItem[];
  cartResults?: CartResultsState;
};

type MealPlanViewProps = {
  userId: string;
  planId?: string;
  slugWithId?: string;
  onBack?: () => void;
  onDelete?: (mealPlan: MealPlan) => void;
  isDeleting?: boolean;
};

export const MealPlanView = ({ userId, planId, slugWithId, onBack, onDelete, isDeleting }: MealPlanViewProps) => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [pantryItems, setPantryItems] = useState<PantryItem[]>([]);
  
  // Add CSS for Kroger button hover and grocery item interactions
  const buttonStyle = `
    .kroger-cart-button:hover:not(:disabled):not([data-disabled]) {
      background-color: #0f3f7f !important;
      cursor: pointer !important;
    }
    .kroger-cart-button:disabled,
    .kroger-cart-button[data-disabled] {
      cursor: not-allowed !important;
    }
    .kroger-cart-button:disabled:hover,
    .kroger-cart-button[data-disabled]:hover {
      background-color: #e9ecef !important;
      cursor: not-allowed !important;
    }
    .kroger-connect-button:hover {
      background-color: #0f3f7f !important;
    }
    
    /* Always show pantry actions */
    .grocery-item .pantry-actions {
      opacity: 1;
    }
  `;

  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = buttonStyle;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [buttonStyle]);
  const { user, loading: userLoading } = useAppUser();
  // Seed from the plan this route last displayed this session, so a back
  // navigation renders it on the first paint; the mount fetch then refreshes
  // it silently (the loading gate below only shows a skeleton when no plan is
  // on screen).
  const planRouteKey = planId || slugWithId || null;
  const planCacheKey = planRouteKey ? `meal-plan:${planRouteKey}` : null;
  const seededPlan = getPageCache<MealPlanCacheEntry>(planCacheKey);
  const [isOwner, setIsOwner] = useState(
    seededPlan?.owner && user?.uid ? seededPlan.owner.uid === user.uid : user?.uid === userId
  );
  const [mealPlan, setMealPlanState] = useState<MealPlan | null>(seededPlan?.mealPlan ?? null);
  // The route key the currently displayed plan was loaded for — mutations
  // mirror the plan back into the cache under this key. A ref (not the render
  // key) so a plan→plan navigation can't cache the old plan under the new key.
  const planCacheKeyRef = useRef<string | null>(seededPlan ? planCacheKey : null);
  // Freshest grocery progress delivered by the doc listener. The listener is
  // the fast channel for this one field; several handlers replace the plan
  // wholesale from REST while a grocery list generates (substitution
  // refetches, cart flows, the initial load), and those copies captured the
  // doc when their read started. The wrapped setter grafts the freshest copy
  // onto any in-flight plan so none of those paths can rewind the progress
  // bar — the same freshness rule the dashboard applies to generationProgress.
  const liveGroceryProgressRef = useRef<GroceryListGenerationProgress | undefined>(undefined);
  const setMealPlan = useCallback(
    (action: React.SetStateAction<MealPlan | null>) => {
      setMealPlanState((prev) => {
        const next = typeof action === 'function' ? action(prev) : action;
        const status = next?.groceryListStatus?.status;
        if (!next || (status !== 'pending' && status !== 'generating')) {
          return next;
        }
        const winner = freshestProgress(
          next.groceryListProgress,
          liveGroceryProgressRef.current
        );
        return winner === next.groceryListProgress
          ? next
          : { ...next, groceryListProgress: winner };
      });
    },
    []
  );
  // After the plan loads, mealPlan.id is the real Firestore doc ID.
  // planId is only set for legacy URLs; slugWithId is only for the initial fetch.
  const effectivePlanId = mealPlan?.id || planId || '';
  const [owner, setOwner] = useState<{ uid: string; displayName?: string; photoURL?: string } | null>(seededPlan?.owner ?? null);
  // /plan/:slugWithId is server-injected on full load, so keep the tab title
  // empty until the plan loads (avoids clobbering the server <title>); on
  // client-side navigation this then sets a data-driven title.
  usePageTitle(mealPlan ? `Meal Plan (${formatDateRange(mealPlan)}) | Meals You Love` : '');
  // userId prop may be empty on slug routes; after load, use owner or auth user for API calls
  const effectiveUserId = userId || owner?.uid || user?.uid || '';
  const [initialLoading, setInitialLoading] = useState(!seededPlan);
  const [groceryList, setGroceryList] = useState<GroceryItem[]>(seededPlan?.groceryList ?? []);
  const [loadingActivation, setLoadingActivation] = useState(false);
  const [substitutingDays, setSubstitutingDays] = useState<Set<string>>(new Set());
  // Days whose assignment we've actually observed in flight (substitutionStatus
  // pending/generating) since the request was made. A substitution only becomes
  // visible on the server a few hundred ms after the request starts, so until a
  // day shows up here, a terminal/absent status means "hasn't started yet"
  // rather than "already finished" — see the substitution listener below.
  const [substitutionsSeenInFlight, setSubstitutionsSeenInFlight] = useState<Set<string>>(new Set());
  const substitutionStartedAtRef = useRef<Map<string, number>>(new Map());
  // Live AI-substitution progress by plannedDay, read straight off the
  // in-flight assignment snapshots. Kept out of the plan state on purpose:
  // progress writes land every few hundred ms while the model streams, and
  // patching the plan for each one would rebuild every card. The ref mirrors
  // the state for freshness checks (a resubscribe can replay an older cached
  // snapshot) without making the listener depend on its own output.
  const [substitutionProgressByKey, setSubstitutionProgressByKey] =
    useState<Record<string, SubstitutionGenerationProgress>>({});
  const liveSubstitutionProgressRef = useRef<Map<string, SubstitutionGenerationProgress>>(new Map());
  // Which days the in-flight query held on its last snapshot, serialized for
  // cheap comparison. Progress-only snapshots keep the same set; a day
  // entering or leaving it is what warrants a REST refetch. Null forces the
  // next snapshot to refetch regardless: it is set on each new request so a
  // substitution that reached a terminal status before the first snapshot
  // (the query never saw it) still gets its refetch.
  const inFlightDaysKeyRef = useRef<string | null>('');
  const [swappingDays, setSwappingDays] = useState<Set<string>>(new Set());
  const pendingSwapCallbacksRef = useRef<Map<string, () => Promise<void>>>(new Map());
  // Optimistic preview: maps cook day plannedDate → currently-visible carousel recipe (for instant prep day updates)
  const [cookDayPreviews, setCookDayPreviews] = useState<Record<string, Recipe>>({});
  const [savingDays, setSavingDays] = useState<Set<string>>(new Set());
  const [activeShoppingTab, setActiveShoppingTab] = useState<string | null>(() => {
    // Try to restore from localStorage, default to 'all' if nothing saved
    try {
      return localStorage.getItem('activeShoppingTab') || 'all';
    } catch {
      return 'all';
    }
  });

  // Function to update shopping tab and persist to localStorage
  const updateActiveShoppingTab = (tab: string) => {
    setActiveShoppingTab(tab);
    try {
      localStorage.setItem('activeShoppingTab', tab);
    } catch {
      // Ignore localStorage errors
    }
  };
  
  // Kroger cart integration state
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  
  // Instacart integration state
  const [addingToInstacart, setAddingToInstacart] = useState(false);
  
  // Shopping list drawer state
  const [shoppingListDrawerOpen, setShoppingListDrawerOpen] = useState(false);

  // Calendar sync state
  const [loadingCalendarSync, setLoadingCalendarSync] = useState(false);

  // Additional items drawer state
  const [additionalItemsDrawerOpen, setAdditionalItemsDrawerOpen] = useState(false);
  const [savingAdditionalItems, setSavingAdditionalItems] = useState(false);

  // Nutrition drawer state
  const [nutritionDrawerOpened, setNutritionDrawerOpened] = useState(false);
  const [selectedRecipeForNutrition, setSelectedRecipeForNutrition] = useState<Recipe | null>(null);
  
  // Mobile breakpoint
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Rows only need equalizing when plan cards sit side by side. Matches the
  // grid's md breakpoint (62em); below it the layout is single-column and each
  // card takes its natural height — otherwise short cards (penciled-in days)
  // stretch to the tallest recipe card. Read immediately so the desktop grid
  // doesn't flash unequal rows on mount.
  const isTwoColumnPlanGrid = useMediaQuery('(min-width: 62em)', true, {
    getInitialValueInEffect: false,
  });

  // Undo functionality for deleted assignments
  const [deletedAssignments, setDeletedAssignments] = useState<Set<string>>(new Set());
  // Dates whose restore call is in flight (undo toast or placeholder card)
  const [restoringDays, setRestoringDays] = useState<Set<string>>(new Set());
  // Target of the add/edit custom meal drawer; null = closed
  const [customMealTarget, setCustomMealTarget] = useState<
    | { mode: 'add'; plannedDate: string; plannedDay: string; mealType: MealType; replacingName?: string }
    | { mode: 'edit'; assignment: MealPlanAssignment }
    | null
  >(null);
  const [savingCustomMeal, setSavingCustomMeal] = useState(false);
  // Remembered penciled-in meals; fetched once when the sheet first opens,
  // then kept current locally after each save
  const [savedPenciledMeals, setSavedPenciledMeals] = useState<PenciledMealMemory[] | null>(null);
  const [cartProgress, setCartProgress] = useState<{
    step: 'pending' | 'searching_products' | 'matching_products' | 'adding_to_cart' | 'completed' | 'failed';
    message: string;
    itemsProcessed?: number;
    totalItems?: number;
    matchingProcessed?: number;
    matchingTotal?: number;
  }>({ step: 'pending', message: 'Preparing...' });
  // Seed from the merged app user (which carries the session-cached profile
  // on remounts) so profile-driven UI — like the Kroger vs Instacart grocery
  // button — renders correctly on the first paint after a back navigation
  // instead of flipping when the fetch below lands.
  const [userProfile, setUserProfile] = useState<FamilyProfile | null>(user?.familyProfile ?? null);
  // Cart state seeds with the cached plan so the shopping-list card resumes
  // exactly as last shown — "View in Instacart" link, "items added to cart"
  // summary — instead of those popping in when the background cart-status
  // check completes. In-flight cart additions are deliberately not seeded;
  // the background check re-detects and resumes them live.
  const [cartResults, setCartResults] = useState<CartResultsState>(
    seededPlan?.cartResults ?? null
  );
  const [initialCartCheckComplete, setInitialCartCheckComplete] = useState(!!seededPlan);
  
  // Recipe substitution state
  const [substitutionModalOpen, setSubstitutionModalOpen] = useState(false);
  const [selectedDayForSubstitution, setSelectedDayForSubstitution] = useState<string>('');
  // Which slot within the day is being swapped ('dinner' for the hero card;
  // breakfast/lunch rows set their own)
  const [selectedMealTypeForSubstitution, setSelectedMealTypeForSubstitution] = useState<MealType>('dinner');
  const [substitutionContext, setSubstitutionContext] = useState('');
  const [substitutionReasons, setSubstitutionReasons] = useState<string[]>([]);
  // Opt-in on the swap sheet: promote the tapped ingredient chips from a
  // one-meal rejection to a standing entry in foods to avoid.
  const [substitutionAlwaysAvoid, setSubstitutionAlwaysAvoid] = useState(false);
  const [modifyExisting, setModifyExisting] = useState(false); // Swap sheet defaults to replace

  // Recipe selection state
  const [recipeSelectionModalOpen, setRecipeSelectionModalOpen] = useState(false);
  const [selectedDayForRecipeSelection, setSelectedDayForRecipeSelection] = useState<string>('');
  const [selectedMealTypeForRecipeSelection, setSelectedMealTypeForRecipeSelection] = useState<MealType>('dinner');
  // Cookbook for the add/swap drawer, seeded from the session's shared list
  // cache so reopening the drawer renders instantly and refreshes silently
  const [userRecipes, setUserRecipes] = useState<Recipe[]>(
    () => getCachedRecipeList('cookbook', user?.uid) ?? []
  );
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  
  // Tooltip state for mobile click handling
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  
  // Meal plan context menu state
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false);
  
  // Feedback modal state
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackContext, setFeedbackContext] = useState<{
    type: 'meal_plan' | 'assignment';
    mealPlanId: string;
    assignmentId?: string;
    recipeName?: string;
    mealPlanDateRange?: string;
  } | null>(null);

  // Recipe rating state
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [ratingAssignment, setRatingAssignment] = useState<MealPlanAssignment | null>(null);
  const [ratingDays, setRatingDays] = useState<Set<string>>(new Set());

  const [servingsInputMode, setServingsInputMode] = useState<string | null>(null);
  const [tempServings, setTempServings] = useState<number>(1);
  const [menuOpen, setMenuOpen] = useState<{[key: string]: boolean}>({});
  
  // Product preference state
  const [expandedPreferenceItems, setExpandedPreferenceItems] = useState<Set<string>>(new Set());
  // Bumped on every optimistic grocery-list mutation; lets in-flight toggle
  // responses detect they are stale and skip applying their full-list snapshot
  const groceryMutationSeqRef = useRef(0);

  // Past plans (end date passed) are read-only, whether they're expired
  // drafts or completed active plans. The backend rejects modification too.
  const planIsPast = mealPlan ? isMealPlanPast(mealPlan) : false;

  // Local state to track if draft tour has been completed in this session
  const [hasCompletedDraftTour, setHasCompletedDraftTour] = React.useState(false);

  // Tour state - check if user has completed the tour and if this is a draft plan (first plan)
  const shouldShowTour = React.useMemo(() => {
    return isOwner &&
           mealPlan?.status === 'draft' &&
           !planIsPast &&
           !user?.completedTours?.mealPlanDraft &&
           !hasCompletedDraftTour &&
           !initialLoading &&
           !userLoading &&
           mealPlan.assignments &&
           mealPlan.assignments.length > 0;
  }, [isOwner, mealPlan?.status, planIsPast, user?.completedTours, hasCompletedDraftTour, initialLoading, userLoading, mealPlan?.assignments]);

  // Check if any assignment has alternatives (carousel)
  const hasCarousel = React.useMemo(() => {
    return mealPlan?.assignments?.some(a => a.alternatives && a.alternatives.length > 0) ?? false;
  }, [mealPlan?.assignments]);

  const markTourCompleted = React.useCallback(async (
    tourType: 'mealPlanDraft' | 'mealPlanActivated' | 'firstSwapPrompt'
  ) => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/tour-completed`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tourType }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`Failed to mark ${tourType} as completed:`, response.status, errorData);
      }
    } catch (error) {
      console.error('Failed to mark tour as completed:', error);
    }
  }, [user]);

  const handleTourComplete = React.useCallback(async () => {
    // Immediately mark as completed locally
    setHasCompletedDraftTour(true);
    await markTourCompleted('mealPlanDraft');
  }, [markTourCompleted]);

  useMealPlanTour(shouldShowTour, handleTourComplete);

  // Local state to track if activation tour has been completed in this session
  const [hasCompletedActivatedTour, setHasCompletedActivatedTour] = React.useState(false);

  // Activated tour state - check if user has completed the tour and if grocery list is ready
  const shouldShowActivatedTour = React.useMemo(() => {
    return isOwner &&
           mealPlan?.status === 'active' &&
           !planIsPast &&
           mealPlan?.groceryListStatus?.status === 'completed' &&
           !user?.completedTours?.mealPlanActivated &&
           !hasCompletedActivatedTour &&
           !initialLoading &&
           !userLoading &&
           groceryList &&
           groceryList.length > 0;
  }, [isOwner, mealPlan?.status, planIsPast, mealPlan?.groceryListStatus?.status, user?.completedTours, hasCompletedActivatedTour, initialLoading, userLoading, groceryList]);

  const handleActivatedTourComplete = React.useCallback(async () => {
    // Immediately mark as completed locally
    setHasCompletedActivatedTour(true);
    await markTourCompleted('mealPlanActivated');
  }, [markTourCompleted]);

  const krogerConnected = React.useMemo(() => {
    return user?.familyProfile?.krogerConnected ?? false;
  }, [user?.familyProfile?.krogerConnected]);

  // Everything the user has already told us to keep out of their plans. The
  // swap sheet uses it to suppress "Don't want X" chips for things X is
  // already on the list for.
  const substitutionAvoidTerms = React.useMemo(
    () => [
      ...(user?.familyProfile?.dislikes ?? []),
      ...(user?.familyProfile?.allergies ?? []),
      ...(user?.familyProfile?.avoidProteins ?? []),
    ],
    [
      user?.familyProfile?.dislikes,
      user?.familyProfile?.allergies,
      user?.familyProfile?.avoidProteins,
    ]
  );

  useMealPlanActivatedTour(shouldShowActivatedTour, handleActivatedTourComplete, krogerConnected);

  // First-swap prompt: one-shot interactive replacement for the old carousel
  // tour slide. On a draft plan with alternatives, once the draft tour is done,
  // frame the plan as a starting point and direct the user to the real swap
  // gesture (swipe / "more ideas" chip). Completes when a swap settles.
  const [firstSwapPromptDone, setFirstSwapPromptDone] = React.useState(false);

  // Touch-primary devices swipe; mouse/trackpad devices use the arrows and click
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const shouldShowFirstSwapPrompt = React.useMemo(() => {
    return isOwner &&
           mealPlan?.status === 'draft' &&
           !planIsPast &&
           hasCarousel &&
           (hasCompletedDraftTour || !!user?.completedTours?.mealPlanDraft) &&
           !user?.completedTours?.firstSwapPrompt &&
           !firstSwapPromptDone &&
           !initialLoading &&
           !userLoading;
  }, [isOwner, mealPlan?.status, planIsPast, hasCarousel, hasCompletedDraftTour, user?.completedTours, firstSwapPromptDone, initialLoading, userLoading]);

  const completeFirstSwapPrompt = React.useCallback((
    outcome: 'engaged' | 'dismissed',
    plannedDay?: string,
    mealType?: MealType
  ) => {
    setFirstSwapPromptDone(true);
    posthog?.capture(
      outcome === 'engaged'
        ? AnalyticsEvent.FirstSwapPromptEngaged
        : AnalyticsEvent.FirstSwapPromptDismissed,
      plannedDay
        ? { planned_day: plannedDay, ...(mealType ? { meal_type: mealType } : {}) }
        : undefined
    );
    markTourCompleted('firstSwapPrompt');
  }, [posthog, markTourCompleted]);

  // Capture once per session when the prompt becomes visible
  const firstSwapShownRef = React.useRef(false);
  React.useEffect(() => {
    if (shouldShowFirstSwapPrompt && !firstSwapShownRef.current) {
      firstSwapShownRef.current = true;
      posthog?.capture(AnalyticsEvent.FirstSwapPromptShown);
    }
  }, [shouldShowFirstSwapPrompt, posthog]);

  // Transition only animates a mounted-prop CHANGE — if the banner is eligible
  // on the component's first real render (post-skeleton), an initially-true
  // value would appear with no entrance. Start false and flip after a beat.
  const [firstSwapBannerVisible, setFirstSwapBannerVisible] = React.useState(false);
  React.useEffect(() => {
    if (shouldShowFirstSwapPrompt) {
      const timer = setTimeout(() => setFirstSwapBannerVisible(true), 600);
      return () => clearTimeout(timer);
    }
    setFirstSwapBannerVisible(false);
  }, [shouldShowFirstSwapPrompt]);

  const {
    getMealPlan,
    activateMealPlan,
    regenerateGroceryList,
    substituteRecipe,
    saveRecipeToUserCookbook,
    removeRecipeFromUserCookbook,
    addMealPlanAssignment,
    deleteMealPlanAssignment,
    undoDeleteMealPlanAssignment,
    addCustomMealAssignment,
    updateCustomMealAssignment,
    getPenciledMeals,
    updateMealPlan,
    loading,
    error
  } = useMealPlanning();

  // Drag-and-drop state for reordering recipes between days
  const [activeAssignmentId, setActiveAssignmentId] = useState<string | null>(null);
  // Which meal slot the grid is showing (plans without breakfast/lunch never
  // render the control, so this stays 'dinner'). Kept in the URL (?meal=...)
  // rather than state so navigating into a recipe and back restores the tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const mealParam = searchParams.get('meal');
  const activeMealTab: MealType | 'cooks' =
    mealParam === 'breakfast' || mealParam === 'lunch' || mealParam === 'cooks'
      ? mealParam
      : 'dinner';
  const setActiveMealTab = useCallback(
    (tab: MealType | 'cooks') => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (tab === 'dinner') {
            next.delete('meal'); // dinner is the default; keep the URL clean
          } else {
            next.set('meal', tab);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  // Empty-slot fills in flight, keyed `date#mealType`
  const [addingSlots, setAddingSlots] = useState<Set<string>>(new Set());
  // When set, the recipe selection drawer is filling an empty slot instead of
  // substituting an existing assignment
  const [addTarget, setAddTarget] = useState<{ date: string; mealType: MealType } | null>(null);
  // Tracks which recipe (if any) each card is flipped to, so the drag overlay can
  // start on the same face as the card being dragged. A ref avoids re-rendering on flip.
  const flippedRecipeByAssignmentRef = useRef<Record<string, string | null>>({});

  // Drag-and-drop sensors for recipe reordering
  // Reorder is initiated only from the card's grip handle (see SortableMealCard).
  //
  // Mouse: combined delay + distance constraint - the drag engages on whichever
  // happens first, moving past `distance` (so a deliberate drag feels immediate,
  // no lag) or holding for `delay` (so press-and-hold engages even without
  // moving). A quick click does neither, so it no longer engages. `tolerance`
  // only aborts on an implausibly large single jump, so real drags never cancel.
  //
  // Touch: delay-only press-and-hold. On mobile a finger that brushes the handle
  // while scrolling moves past any small `distance` instantly, so a distance-based
  // constraint engages the drag mid-scroll. Instead we require holding the handle
  // still for `delay` ms; `tolerance` cancels the gesture if the finger moves
  // past that threshold during the hold (i.e. it's a scroll, not a drag).
  const mouseActivation = { delay: 120, distance: 3, tolerance: 50 };
  const touchActivation = { delay: 250, tolerance: 8 };
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: mouseActivation }),
    useSensor(TouchSensor, { activationConstraint: touchActivation }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Whether a cart addition is being actively tracked. Enabled once the
  // backend has (re)initialized cartAdditionStatus, so the listener can't pick
  // up a stale terminal status from a previous run.
  const [cartTrackingActive, setCartTrackingActive] = useState(false);

  // Cart addition statuses read straight from a Firestore snapshot carry
  // Timestamp objects where the REST API returns ISO strings.
  const normalizeCartAdditionStatus = (raw: Record<string, unknown>): CartAdditionStatus => {
    const toIso = (value: unknown) =>
      value && typeof (value as { toDate?: () => Date }).toDate === 'function'
        ? (value as { toDate: () => Date }).toDate().toISOString()
        : value;
    return {
      ...raw,
      startedAt: toIso(raw.startedAt),
      completedAt: toIso(raw.completedAt),
    } as CartAdditionStatus;
  };

  // Apply a cart addition status update to the progress UI. Mirrors the
  // status handling previously performed inside the 2s polling loop; updates
  // now arrive from a realtime listener on the meal plan doc.
  const applyCartAdditionStatus = useCallback((status: CartAdditionStatus) => {
    switch (status.status) {
      case 'pending':
        setCartProgress({
          step: 'pending',
          message: 'Getting your grocery list ready...',
          totalItems: 0
        });
        break;
      case 'searching_products':
        setCartProgress({
          step: 'searching_products',
          message: `Finding your groceries (${status.productSearchProgress.current}/${status.productSearchProgress.total} items)...`,
          itemsProcessed: status.productSearchProgress.current,
          totalItems: status.productSearchProgress.total
        });
        break;
      case 'matching_products':
        setCartProgress({
          step: 'matching_products',
          message: 'Picking the best options for you...',
          itemsProcessed: status.productSearchProgress.total,
          totalItems: status.productSearchProgress.total,
          matchingProcessed: status.productMatchingProgress?.current || 0,
          matchingTotal: status.productMatchingProgress?.total || 0,
        });
        break;
      case 'adding_to_cart':
        setCartProgress({
          step: 'adding_to_cart',
          message: 'Adding everything to your cart...',
          itemsProcessed: status.productSearchProgress.total,
          totalItems: status.productSearchProgress.total
        });
        break;
      case 'completed':
        if (status.results) {
          const addedCount = status.results.addedItems?.length || 0;
          const failedCount = status.results.failedItems?.length || 0;
          setCartProgress({
            step: 'completed',
            message: `Cart update complete! Added ${addedCount} items, ${failedCount} failed.`,
            itemsProcessed: addedCount,
            totalItems: addedCount + failedCount
          });
          setCartResults(status.results);
          setCartModalOpen(true); // Auto-open drawer to show results

          // Update the meal plan with the cart addition status so the success state persists
          setMealPlan(prevPlan => {
            if (!prevPlan) return prevPlan;
            return {
              ...prevPlan,
              cartAdditionStatus: status
            };
          });
        }
        setCartTrackingActive(false);
        setAddingToCart(false);
        break; // Stop tracking
      case 'failed':
        setCartProgress({
          step: 'failed',
          message: status.results?.message || 'Cart addition failed',
          totalItems: 0
        });
        if (status.results) {
          setCartResults(status.results);
          setCartModalOpen(true); // Auto-open drawer to show error details
        }
        setCartTrackingActive(false);
        setAddingToCart(false);
        break; // Stop tracking
    }
  }, [setMealPlan]);

  // Realtime listeners are only possible when the signed-in user owns the
  // plan (security rules grant owners read access); on shared plans the
  // trigger hook falls back to interval polling of the REST API.
  const canListenToPlan = !!user?.uid && !!effectivePlanId && user.uid === effectiveUserId;
  const planDocRef = useMemo(
    () => (canListenToPlan ? doc(db, 'users', effectiveUserId, 'meal_plans', effectivePlanId) : null),
    [canListenToPlan, effectiveUserId, effectivePlanId]
  );
  const inFlightAssignmentsQuery = useMemo(
    () =>
      canListenToPlan
        ? query(
            collection(db, 'users', effectiveUserId, 'meal_plans', effectivePlanId, 'assignments'),
            where('substitutionStatus.status', 'in', ['pending', 'generating'])
          )
        : null,
    [canListenToPlan, effectiveUserId, effectivePlanId]
  );

  // Track cart addition progress while one is in flight: the meal plan doc
  // carries cartAdditionStatus, so snapshot updates stream progress directly;
  // fallback ticks read it over REST instead.
  useFirestoreTrigger({
    enabled: cartTrackingActive,
    target: planDocRef,
    fallbackIntervalMs: 2000,
    onTrigger: async (snapshot) => {
      try {
        if (snapshot && !('docs' in snapshot)) {
          const raw = snapshot.data()?.cartAdditionStatus;
          if (raw) {
            applyCartAdditionStatus(normalizeCartAdditionStatus(raw));
          }
          return;
        }
        if (!user) return;
        const token = await user.getIdToken();
        applyCartAdditionStatus(await getCartAdditionStatus(effectivePlanId, token));
      } catch (error) {
        console.error('Error checking cart addition status:', error);
        setCartProgress({
          step: 'failed',
          message: 'Error checking cart addition status',
          totalItems: 0
        });
        setCartTrackingActive(false);
        setAddingToCart(false);
      }
    },
  });

  // Preserve the old polling loop's 5-minute cap: if the cart addition never
  // reaches a terminal status, surface a timeout instead of waiting forever.
  useEffect(() => {
    if (!cartTrackingActive) return;
    const timeoutId = setTimeout(() => {
      setCartProgress({
        step: 'failed',
        message: 'Cart addition timed out. Please try again.',
        totalItems: 0
      });
      setCartTrackingActive(false);
      setAddingToCart(false);
    }, 5 * 60 * 1000);
    return () => clearTimeout(timeoutId);
  }, [cartTrackingActive]);

  // Fetch pantry items to check availability - needs to be defined before useEffect
  const fetchPantryItems = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPantryItems(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching pantry items:', error);
    }
  }, [user]);

  useEffect(() => {
    const fetchMealPlan = async () => {
      // Wait for user auth to finish loading before making the request
      // This ensures the auth token is included if the user is authenticated
      if (userLoading) {
        return;
      }

      const data = await getMealPlan(userId, planId || slugWithId!);
      setInitialLoading(false);

      if (data) {
        // From here on, mutations to the displayed plan mirror into the
        // session cache under this route's key (see the mirror effect below).
        planCacheKeyRef.current = `meal-plan:${planId || slugWithId}`;
        setMealPlan(data.mealPlan);
        setOwner(data.owner || null);
        if (data.owner?.uid && user?.uid) {
          setIsOwner(data.owner.uid === user.uid);
        }

        // If grocery list is already completed, show it
        if (data.mealPlan.groceryListStatus?.status === 'completed' && data.mealPlan.groceryList) {
          setGroceryList(data.mealPlan.groceryList);
        }

        // Check for existing cart addition status
        if (user) {
          try {
            const token = await user.getIdToken();
            const cartStatus = await getCartAdditionStatus(data.mealPlan.id, token);
            
            if (cartStatus) {
              // Update progress based on current status
              switch (cartStatus.status) {
                case 'pending':
                case 'searching_products':
                case 'matching_products':
                case 'adding_to_cart':
                  setAddingToCart(true);
                  setCartProgress({
                    step: cartStatus.status,
                    message: `Cart addition in progress...`,
                    itemsProcessed: cartStatus.productSearchProgress?.current || 0,
                    totalItems: cartStatus.productSearchProgress?.total || 0
                  });
                  // Start realtime tracking to continue following progress
                  setCartTrackingActive(true);
                  break;
                case 'completed':
                case 'failed':
                  setAddingToCart(false);
                  if (cartStatus.results) {
                    setCartResults(cartStatus.results);
                    setCartProgress({
                      step: cartStatus.status,
                      message: cartStatus.status === 'completed' ? 'Cart update complete!' : 'Cart addition failed',
                      totalItems: (cartStatus.results.addedItems?.length || 0) + (cartStatus.results.failedItems?.length || 0)
                    });
                  }
                  break;
              }
            } else {
              // No cart status on the server — clear any seeded results so a
              // stale "items added" summary can't outlive the real state.
              setCartResults(null);
            }
          } catch (error) {
            console.error('Failed to check cart addition status:', error);
            // Don't throw - this is not critical for page load
          } finally {
            setInitialCartCheckComplete(true);
          }
        } else {
          setInitialCartCheckComplete(true);
        }
      }
    };
    fetchMealPlan();

    // Also fetch pantry items when component loads
    fetchPantryItems();
  }, [planId, slugWithId, getMealPlan, user, fetchPantryItems, userId, userLoading, setMealPlan]);

  // Mirror the displayed plan into the session cache so substitutions,
  // grocery updates, and other mutations are what a back navigation resumes
  // from. Keyed off the ref set when a plan for this route loaded (or was
  // seeded), never off the current URL, so a plan→plan navigation can't cache
  // the outgoing plan under the incoming route's key.
  useEffect(() => {
    if (planCacheKeyRef.current && mealPlan) {
      setPageCache(planCacheKeyRef.current, { mealPlan, owner, groceryList, cartResults });
    }
  }, [mealPlan, owner, groceryList, cartResults]);

  // Realtime refresh for async generation: refetch the composed plan whenever
  // the plan doc changes while generation is in flight.
  const generationInFlight = !!mealPlan &&
    mealPlan.generationStatus.status !== 'completed' &&
    mealPlan.generationStatus.status !== 'failed';
  useFirestoreTrigger({
    enabled: generationInFlight,
    target: planDocRef,
    onTrigger: async () => {
      const data = await getMealPlan(effectiveUserId, effectivePlanId, true); // Skip loading state during background refresh
      if (data && data.mealPlan.generationStatus !== mealPlan?.generationStatus) {
        setMealPlan(data.mealPlan);
      }
    },
  });

  // Kroger connection status comes off the merged app user's profile: seeded
  // above for the first paint, and tracked here as useAppUser's per-mount
  // refresh lands. (No bespoke profile fetch — useAppUser is the one source;
  // the on-demand refetch after picking a Kroger store still updates this
  // state directly.)
  useEffect(() => {
    if (user?.familyProfile) {
      setUserProfile(user.familyProfile);
    }
  }, [user?.familyProfile]);

  // Put a slot into the loading state, keyed by substitutionKey (a bare day is
  // the dinner key). The backend hasn't written substitutionStatus yet at this
  // point, so the key is explicitly marked as not-yet-seen-in-flight and
  // stamped with a start time for the watchdog.
  const beginSubstitution = useCallback((key: string) => {
    substitutionStartedAtRef.current.set(key, Date.now());
    inFlightDaysKeyRef.current = null;
    setSubstitutionsSeenInFlight(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setSubstitutingDays(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);

  const endSubstitution = useCallback((key: string) => {
    substitutionStartedAtRef.current.delete(key);
    setSubstitutionsSeenInFlight(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setSubstitutingDays(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // A successful substitution request means the backend has already written
  // substitutionStatus: pending on the assignment. Reflecting that locally
  // keeps the loading state backed by real status instead of depending on the
  // listener's first snapshot landing after that write.
  const markAssignmentPending = useCallback((day: string, mealType: MealType = 'dinner') => {
    setMealPlan(prev => {
      if (!prev) return prev;
      let changed = false;
      const assignments = prev.assignments.map(assignment => {
        if (assignment.plannedDay !== day) return assignment;
        if (getAssignmentMealType(assignment) !== mealType) return assignment;
        const status = assignment.substitutionStatus?.status;
        if (status === 'pending' || status === 'generating') return assignment;
        changed = true;
        return { ...assignment, substitutionStatus: { status: 'pending' as const } };
      });
      return changed ? { ...prev, assignments } : prev;
    });
  }, [setMealPlan]);

  // Realtime refresh for recipe substitutions - active when there are active
  // substitutions OR when we need to clear completed ones. Watches the plan's
  // in-flight assignment docs: snapshots fire when a substitution starts and
  // when it reaches a terminal status (the doc leaves the query).
  // Loading keys are slot-aware (assignmentSubstitutionKey): a day can hold
  // breakfast/lunch/dinner assignments, so the bare day string is ambiguous.
  const hasActiveSubstitution = !!mealPlan && mealPlan.assignments.some(
    assignment => assignment.substitutionStatus?.status === 'pending' || assignment.substitutionStatus?.status === 'generating'
  );
  const hasLoadingDaysToClear = !!mealPlan && substitutingDays.size > 0 && mealPlan.assignments.some(assignment =>
    substitutingDays.has(assignmentSubstitutionKey(assignment)) &&
    (assignment.substitutionStatus?.status === 'completed' || assignment.substitutionStatus?.status === 'failed' || !assignment.substitutionStatus)
  );
  useFirestoreTrigger({
    enabled: hasActiveSubstitution || hasLoadingDaysToClear,
    target: inFlightAssignmentsQuery,
    onTrigger: async (snapshot) => {
      if (!mealPlan) return;
      // The query only ever contains assignments the backend has marked in
      // flight, so any slot present here has definitely started.
      const seenInFlight = new Set<string>();
      if (snapshot && 'docs' in snapshot) {
        // Substitution progress rides on the same in-flight docs, so it is
        // read straight off the snapshot — an AI swap streams a progress
        // write every few hundred ms, and refetching per trigger would turn
        // each one into a REST call. Only a structural change (a day
        // entering or leaving the in-flight set) warrants the refetch below,
        // because that is when the recipe itself has changed.
        const nextProgress = new Map<string, SubstitutionGenerationProgress>();
        snapshot.docs.forEach(docSnapshot => {
          const data = docSnapshot.data();
          const plannedDay = data?.plannedDay;
          if (typeof plannedDay !== 'string') return;
          // Both keyed slot-aware: breakfast/lunch AI fallbacks stream
          // progress too, and a bare-day key would cross-render a same-day
          // dinner's narration onto them
          const progressKey = substitutionKey(plannedDay, getAssignmentMealType(data ?? {}));
          seenInFlight.add(progressKey);
          const snapProgress = data?.substitutionProgress as SubstitutionGenerationProgress | undefined;
          const winner = snapProgress === undefined
            ? undefined
            : freshestProgress(liveSubstitutionProgressRef.current.get(progressKey), snapProgress);
          if (winner) nextProgress.set(progressKey, winner);
        });
        // Days absent from the query reached a terminal status; their
        // progress must not linger, or a later swap on the same day would
        // open on the previous run's recipe name.
        liveSubstitutionProgressRef.current = nextProgress;
        setSubstitutionProgressByKey(prev => {
          const days = Array.from(nextProgress.keys());
          const unchanged =
            days.length === Object.keys(prev).length &&
            days.every(day => prev[day] === nextProgress.get(day));
          return unchanged ? prev : Object.fromEntries(nextProgress);
        });

        if (seenInFlight.size > 0) {
          setSubstitutionsSeenInFlight(prev => {
            const next = new Set(prev);
            let hasChanges = false;
            seenInFlight.forEach(day => {
              if (!next.has(day)) {
                next.add(day);
                hasChanges = true;
              }
            });
            return hasChanges ? next : prev;
          });
        }

        const daysKey = Array.from(seenInFlight).sort().join('|');
        if (daysKey === inFlightDaysKeyRef.current) return;
        inFlightDaysKeyRef.current = daysKey;
      }

      const data = await getMealPlan(effectiveUserId, effectivePlanId, true); // Skip loading state during background refresh
      if (data) {
        data.mealPlan.assignments.forEach(assignment => {
          if (assignment.substitutionStatus?.status === 'pending' ||
              assignment.substitutionStatus?.status === 'generating') {
            seenInFlight.add(assignmentSubstitutionKey(assignment));
          }
        });
        if (seenInFlight.size > 0) {
          setSubstitutionsSeenInFlight(prev => {
            const next = new Set(prev);
            let hasChanges = false;
            seenInFlight.forEach(key => {
              if (!next.has(key)) {
                next.add(key);
                hasChanges = true;
              }
            });
            return hasChanges ? next : prev;
          });
        }

        // Check if any substitution status has changed or any recipe content has changed
        const statusChanged = data.mealPlan.assignments.some((updatedAssignment) => {
          const currentAssignment = mealPlan.assignments.find(a => a.id === updatedAssignment.id);
          const updatedRecipe = getAssignmentRecipe(updatedAssignment, data.mealPlan);
          const currentRecipe = currentAssignment ? getAssignmentRecipe(currentAssignment, mealPlan) : undefined;
          return currentAssignment && (
            updatedAssignment.substitutionStatus !== currentAssignment.substitutionStatus ||
            updatedRecipe?.name !== currentRecipe?.name // Recipe content changed
          );
        });

        if (statusChanged) {
          setMealPlan(data.mealPlan);
        }

        // Clear substitutingDays for assignments that are done - but only once
        // the substitution was actually observed in flight. Snapshots (and
        // fallback ticks) fire immediately on subscribe, which can be before
        // the backend has written substitutionStatus; clearing then would drop
        // the spinner off a substitution that is still on its way.
        setSubstitutingDays(prev => {
          const newSet = new Set(prev);
          let hasChanges = false;
          data.mealPlan.assignments.forEach(assignment => {
            const key = assignmentSubstitutionKey(assignment);
            const startedOnServer = substitutionsSeenInFlight.has(key) ||
              seenInFlight.has(key) ||
              !substitutionStartedAtRef.current.has(key);
            if ((assignment.substitutionStatus?.status === 'completed' ||
                assignment.substitutionStatus?.status === 'failed' ||
                !assignment.substitutionStatus) &&
                startedOnServer &&
                newSet.has(key)) {
              newSet.delete(key);
              substitutionStartedAtRef.current.delete(key);
              hasChanges = true;
            }
          });
          return hasChanges ? newSet : prev;
        });
      }
    },
  });

  // Watchdog for substitutions that never show up in flight: if the request
  // succeeded but the backend never wrote substitutionStatus, nothing would
  // fire the listener again, so drop the loading state after a grace period.
  useEffect(() => {
    const waiting = Array.from(substitutingDays).filter(key =>
      !substitutionsSeenInFlight.has(key) && substitutionStartedAtRef.current.has(key)
    );
    if (waiting.length === 0) return;

    const now = Date.now();
    const delay = Math.max(0, Math.min(...waiting.map(key =>
      (substitutionStartedAtRef.current.get(key) ?? now) + SUBSTITUTION_START_GRACE_MS - now
    )));

    const timeoutId = setTimeout(() => {
      setSubstitutingDays(prev => {
        const next = new Set(prev);
        let hasChanges = false;
        waiting.forEach(key => {
          const startedAt = substitutionStartedAtRef.current.get(key);
          if (startedAt === undefined || Date.now() - startedAt < SUBSTITUTION_START_GRACE_MS) return;
          if (!next.has(key)) return;
          next.delete(key);
          substitutionStartedAtRef.current.delete(key);
          hasChanges = true;
        });
        return hasChanges ? next : prev;
      });
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [substitutingDays, substitutionsSeenInFlight]);

  // Realtime refresh for grocery list generation - only active while the
  // grocery list is being generated; the plan doc carries groceryListStatus
  // and, while the model writes, a groceryListProgress snapshot every few
  // hundred milliseconds. Progress and status are read straight off the doc
  // snapshot: refetching per trigger would turn every progress write into a
  // REST call. Only the flip to completed refetches, because the grocery
  // list itself lives in a subcollection the plan doc does not carry.
  const groceryListInFlight = !!mealPlan &&
    (mealPlan.groceryListStatus?.status === 'pending' || mealPlan.groceryListStatus?.status === 'generating');
  useFirestoreTrigger({
    enabled: groceryListInFlight,
    target: planDocRef,
    onTrigger: async (snapshot) => {
      if (snapshot && !('docs' in snapshot)) {
        const data = snapshot.data();
        if (!data) return;
        const nextStatus = data.groceryListStatus as MealPlan['groceryListStatus'];
        const snapProgress = data.groceryListProgress as MealPlan['groceryListProgress'];

        // The snapshot is the authority for whether progress exists at all —
        // a regenerate or retry deletes the field, and a stale ref must not
        // resurrect the previous run's items. Freshness only guards against
        // a resubscribe replaying an older cached snapshot.
        liveGroceryProgressRef.current =
          snapProgress === undefined
            ? undefined
            : freshestProgress(liveGroceryProgressRef.current, snapProgress);
        const nextProgress = liveGroceryProgressRef.current;

        setMealPlan(prevPlan =>
          prevPlan
            ? { ...prevPlan, groceryListStatus: nextStatus, groceryListProgress: nextProgress }
            : prevPlan
        );

        if (
          nextStatus?.status === 'completed' &&
          mealPlan?.groceryListStatus?.status !== 'completed'
        ) {
          const fresh = await getMealPlan(effectiveUserId, effectivePlanId, true); // Skip loading state during background refresh
          if (fresh?.mealPlan.groceryList) {
            setMealPlan(fresh.mealPlan);
            setGroceryList(fresh.mealPlan.groceryList);
          }
        }
        return;
      }

      // Polling fallback (shared plans): no snapshot to read, so refetch as
      // the old polling loop did.
      const data = await getMealPlan(effectiveUserId, effectivePlanId, true); // Skip loading state during background refresh
      if (data && data.mealPlan.groceryListStatus?.status !== mealPlan?.groceryListStatus?.status) {
        setMealPlan(data.mealPlan);

        // If completed, show the grocery list
        if (data.mealPlan.groceryListStatus?.status === 'completed' && data.mealPlan.groceryList) {
          setGroceryList(data.mealPlan.groceryList);
        }
      }
    },
  });

  const handleActivatePlan = async () => {
    if (!mealPlan || planIsPast) return;

    setLoadingActivation(true);
    try {
      // Flush any pending carousel swaps so the server has the correct selected recipes
      const pendingCallbacks = Array.from(pendingSwapCallbacksRef.current.values());
      pendingSwapCallbacksRef.current.clear();
      if (pendingCallbacks.length > 0) {
        await Promise.all(pendingCallbacks.map(cb => cb()));
      }

      // Activate the meal plan (this kicks off async grocery list generation)
      const success = await activateMealPlan(mealPlan.id);
      if (success) {
        posthog?.capture(AnalyticsEvent.MealPlanActivated, {
          meal_plan_id: mealPlan.id,
        });
        // Use functional updater to build on latest state (which may include
        // a flushed carousel swap that updated the selected recipe)
        setMealPlan(prev => prev ? {
          ...prev,
          status: 'active',
          groceryListStatus: {
              ...prev.groceryListStatus,
              status: 'pending',
          }
        } : prev);
      }
    } catch (error) {
      console.error('Failed to activate meal plan:', error);
      // TODO: Show error toast/notification
    } finally {
      setLoadingActivation(false);
    }
  };

  const handleRegenerateGroceryList = async () => {
    if (!mealPlan) return;

    setLoadingActivation(true);
    try {
      const success = await regenerateGroceryList(mealPlan.id);
      if (success) {
        // Clear grocery list and update status to trigger polling
        setGroceryList([]);
        setCartResults(null); // Clear cart results badge
        setMealPlan({
          ...mealPlan,
          groceryListStatus: {
            ...mealPlan.groceryListStatus,
            status: 'pending',
          },
          cartAdditionStatus: undefined,
          instacartShoppingListCache: undefined,
        });
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to regenerate shopping list',
          color: 'red',
        });
      }
    } catch (error) {
      console.error('Failed to regenerate grocery list:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to regenerate shopping list',
        color: 'red',
      });
    } finally {
      setLoadingActivation(false);
    }
  };

  const handleSyncCalendar = async () => {
    if (!mealPlan || !user) return;
    setLoadingCalendarSync(true);
    try {
      const token = await user.getIdToken();
      await syncMealPlanToCalendar(token, mealPlan.id);
      setMealPlan(prev => prev ? { ...prev, calendarSyncedAt: new Date().toISOString() } : prev);
      notifications.show({
        title: 'Synced to calendar',
        message: 'Your meal plan events have been synced to Google Calendar.',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
    } catch (error: any) {
      notifications.show({
        title: 'Sync failed',
        message: error.message || 'Failed to sync to calendar. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setLoadingCalendarSync(false);
    }
  };

  const handleRemoveCalendar = async () => {
    if (!mealPlan || !user) return;
    setLoadingCalendarSync(true);
    try {
      const token = await user.getIdToken();
      await removeMealPlanFromCalendar(token, mealPlan.id);
      setMealPlan(prev => prev ? { ...prev, calendarSyncedAt: undefined } : prev);
      notifications.show({
        title: 'Removed from calendar',
        message: 'Calendar events for this meal plan have been removed.',
        color: 'blue',
        icon: <IconCheck size={18} />,
      });
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to remove calendar events. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setLoadingCalendarSync(false);
    }
  };

  const handleToggleShare = async (isShared: boolean) => {
    if (!mealPlan || !user) return;

    // Optimistically update UI
    const previousState = mealPlan.isShared;
    setMealPlan({ ...mealPlan, isShared });

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/meals/plans/${mealPlan.id}/share`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ isShared }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update sharing status');
      }
    } catch (error) {
      console.error('Error toggling meal plan sharing:', error);
      // Revert on error
      setMealPlan({ ...mealPlan, isShared: previousState });
      notifications.show({
        title: 'Failed',
        message: 'Failed to update sharing status',
        color: 'red',
        icon: <IconX size={18} />,
        autoClose: 5000,
      });
    }
  };

  const handleShareLink = () => {
    if (!mealPlan) return;
    void shareLink({
      title: `Meal plan · ${formatDateRange(mealPlan, true)}`,
      url: `${window.location.origin}${buildPlanPath(mealPlan, userId)}`,
      copyMessage: 'Meal plan link copied to clipboard',
    });
  };

  // Opens the swap sheet (chips + optional instructions + cookbook path)
  const handleSubstituteWithInstructions = (day: string, mealType: MealType = 'dinner') => {
    setSelectedDayForSubstitution(day);
    setSelectedMealTypeForSubstitution(mealType);
    setSubstitutionContext('');
    setSubstitutionReasons([]);
    setSubstitutionAlwaysAvoid(false);
    setModifyExisting(false); // Default to replace; "tweak" is the opt-in
    setSubstitutionModalOpen(true);
  };

  // Fill an empty breakfast/lunch slot. Without a recipeId the backend picks
  // one (catalog-first, single AI generation as fallback); the response is the
  // updated plan, so no watcher wiring is needed.
  const handleAddSlotMeal = async (date: string, mealType: MealType, newRecipeId?: string) => {
    if (!mealPlan) return;
    const key = `${date}#${mealType}`;
    setAddingSlots(prev => new Set(prev).add(key));
    try {
      const updated = await addMealPlanAssignment(mealPlan.id, date, mealType, newRecipeId);
      if (updated) {
        setMealPlan(updated);
      } else {
        notifications.show({
          title: 'Error',
          message: `Failed to add a ${mealType}. Please try again.`,
          color: 'red',
        });
      }
    } finally {
      setAddingSlots(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleAddFromCookbook = async (date: string, mealType: MealType) => {
    // Clear any leftover substitution context (e.g. a swap drawer opened and
    // closed earlier) so the drawer doesn't render a "replacing ..." header
    // for an add
    setSelectedDayForRecipeSelection('');
    setSelectedMealTypeForRecipeSelection('dinner');
    setAddTarget({ date, mealType });
    setRecipeSelectionModalOpen(true);
    await fetchCookbookRecipes();
  };

  const handleSubstituteFromCookbook = async (day: string, mealType: MealType = 'dinner') => {
    setSelectedDayForRecipeSelection(day);
    setSelectedMealTypeForRecipeSelection(mealType);
    setRecipeSelectionModalOpen(true);

    // Always revalidate the cookbook when opening the drawer (silently when
    // there's a list to show)
    await fetchCookbookRecipes();
  };

  const handleServingsInputMode = (day: string, currentServings: number) => {
    setServingsInputMode(day);
    setTempServings(currentServings);
  };

  const handleSubmitServings = async (day: string, mealType: MealType = 'dinner') => {
    if (!mealPlan || !user) return;

    const loadingKey = substitutionKey(day, mealType);

    // Close menu and clear input mode immediately for responsive feel
    setServingsInputMode(null);
    setMenuOpen(prev => ({ ...prev, [day]: false }));

    try {
      beginSubstitution(loadingKey);

      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/meals/plans/${mealPlan.id}/substitute`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dayToReplace: day,
          scaleCurrentRecipe: true,
          targetServings: tempServings,
          ...(mealType !== 'dinner' ? { mealType } : {}),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to change servings');
      }

      markAssignmentPending(day, mealType);
    } catch (error) {
      console.error('Error changing servings:', error);
      endSubstitution(loadingKey);
      notifications.show({
        title: 'Error',
        message: 'Failed to change servings. Please try again.',
        color: 'red',
      });
    }
  };


  const fetchCookbookRecipes = async () => {
    if (!user) return;

    // Skeletons only when there's nothing to show — another surface may have
    // cached the list since this view mounted
    const cached = getCachedRecipeList('cookbook', user.uid);
    if (cached) {
      setUserRecipes(cached);
    }
    setLoadingRecipes(!cached);
    try {
      setUserRecipes(await fetchRecipeList(user, 'cookbook'));
    } catch (error) {
      console.error('Error fetching user recipes:', error);
    } finally {
      setLoadingRecipes(false);
    }
  };

  // Handle optimistic cook day preview for instant prep day updates during carousel swiping
  const handleCookDayPreview = useCallback((plannedDate: string, mealType: MealType, recipe: Recipe | null) => {
    // Keys carry the slot: same-date anchors in different slots must not
    // cross previews (a breakfast swipe previewing on a dinner prep day)
    const key = `${plannedDate}#${mealType}`;
    // Wrap in startTransition so the re-render of all cards doesn't block
    // Embla's carousel animation (keeps slide transitions smooth)
    startTransition(() => {
      setCookDayPreviews(prev => {
        if (recipe) {
          return { ...prev, [key]: recipe };
        }
        // null means user swiped back to the already-selected recipe — remove the preview
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    });
  }, []);

  // Handle swapping an alternative recipe to become the selected recipe
  const handleSwapAlternative = async (assignment: MealPlanAssignment, alternativeRecipe: Recipe, swapSequence: number, targetRecipeIdRef: React.RefObject<string>) => {
    if (!mealPlan || !user) return;

    // Only allow swapping for draft plans
    if (mealPlan.status !== 'draft') {
      notifications.show({
        title: 'Cannot swap',
        message: 'Recipes can only be swapped in draft meal plans',
        color: 'red',
      });
      return;
    }

    const day = assignment.plannedDay;

    // First-swap prompt: a settled swap means the lesson was performed
    if (shouldShowFirstSwapPrompt) {
      completeFirstSwapPrompt('engaged', day, getAssignmentMealType(assignment));
    }

    // Add to swapping set for loading state
    setSwappingDays(prev => new Set(Array.from(prev).concat(day)));

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/meals/plans/${mealPlan.id}/assignments/${assignment.id}/select-alternative`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          recipeId: alternativeRecipe.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to swap recipe');
      }

      const updatedPlan: MealPlan = await response.json();

      // Only update local state if this is still the recipe we're intending to show
      const isStillRelevant = alternativeRecipe.id === targetRecipeIdRef.current;

      if (!isStillRelevant) {
        // User has navigated away, ignore this response
        return;
      }

      // Update with server response to ensure consistency
      setMealPlan(prevPlan => {
        if (!prevPlan) return updatedPlan;

        // Build maps of recipeId -> local state from current assignments
        // This preserves optimistic updates that may not be in the server response yet
        const localSavedState = new Map<string, boolean>();
        const localRatingState = new Map<string, 'up' | 'down'>();
        for (const a of prevPlan.assignments) {
          if (a.scaledRecipe?.isSaved !== undefined) {
            localSavedState.set(a.scaledRecipe.id, a.scaledRecipe.isSaved);
          }
          if (a.scaledRecipe?.rating) {
            localRatingState.set(a.scaledRecipe.id, a.scaledRecipe.rating);
          }
          if (a.alternatives) {
            for (const alt of a.alternatives) {
              if (alt.recipe.isSaved !== undefined) {
                localSavedState.set(alt.recipe.id, alt.recipe.isSaved);
              }
            }
          }
        }

        // Merge every updated assignment, preserving local state. Matched by
        // id: a day can hold several slots, so plannedDay alone is ambiguous.
        // The whole response is merged, not just the swapped assignment:
        // promoting a rotation alternative also rewrites the other
        // recipe-holding days of its block server-side, and keeping those
        // stale left their carousels offering recipes the server no longer
        // held ("Recipe not found in alternatives" on the next swipe there).
        // An assignment with its own swap still pending keeps local state -
        // its own response lands after this one.
        const mergedAssignments = prevPlan.assignments.map(a => {
          const updatedAssignment = updatedPlan.assignments.find(ua => ua.id === a.id);
          if (!updatedAssignment) return a;
          if (a.id !== assignment.id && pendingSwapCallbacksRef.current.has(a.id)) {
            return a;
          }

          // Preserve local state for scaledRecipe
          const mergedScaledRecipe = updatedAssignment.scaledRecipe ? {
            ...updatedAssignment.scaledRecipe,
            isSaved: localSavedState.get(updatedAssignment.scaledRecipe.id) ?? updatedAssignment.scaledRecipe.isSaved,
            rating: localRatingState.get(updatedAssignment.scaledRecipe.id) ?? updatedAssignment.scaledRecipe.rating
          } : updatedAssignment.scaledRecipe;

          // Preserve isSaved from local state for alternatives
          const mergedAlternatives = updatedAssignment.alternatives?.map(alt => ({
            ...alt,
            recipe: {
              ...alt.recipe,
              isSaved: localSavedState.get(alt.recipe.id) ?? alt.recipe.isSaved
            }
          }));

          return {
            ...updatedAssignment,
            scaledRecipe: mergedScaledRecipe,
            alternatives: mergedAlternatives
          };
        });

        return {
          ...prevPlan,
          assignments: mergedAssignments,
        };
      });

      // Clear optimistic cook day preview now that real data is in place
      setCookDayPreviews(prev => {
        const key = `${assignment.plannedDate}#${getAssignmentMealType(assignment)}`;
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      console.error('Error swapping alternative:', error);
      notifications.show({
        title: 'Swap failed',
        message: 'Failed to swap recipe. Please try again.',
        color: 'red',
      });
    } finally {
      setSwappingDays(prev => {
        const newSet = new Set(prev);
        newSet.delete(day);
        return newSet;
      });
    }
  };

  // Close tooltip when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenTooltip(null);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Suppress ResizeObserver errors - this is a common, harmless browser issue
  // Reference: https://trackjs.com/javascript-errors/resizeobserver-loop-completed-with-undelivered-notifications/
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    };
    
    window.addEventListener('error', handleError);
    
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Handle redirection for generation status
  const isGenerating = mealPlan && (mealPlan.generationStatus.status === 'pending' || mealPlan.generationStatus.status === 'generating');
  
  useEffect(() => {
    if (isGenerating && onBack) {
      onBack(); // Go back to dashboard where generation progress is shown via notifications
    }
  }, [isGenerating, onBack]);

  const handleSelectRecipe = async (recipe: Recipe) => {
    if (!mealPlan) return;

    // Drawer opened from an empty-slot cell: create the assignment instead of
    // substituting one
    if (addTarget) {
      const target = addTarget;
      setAddTarget(null);
      setRecipeSelectionModalOpen(false);
      posthog?.capture(AnalyticsEvent.SubstitutionRequested, {
        type: 'slot_add_cookbook',
        meal_type: target.mealType,
      });
      await handleAddSlotMeal(target.date, target.mealType, recipe.id);
      return;
    }

    if (!selectedDayForRecipeSelection) return;

    setRecipeSelectionModalOpen(false);

    const targetMealType = selectedMealTypeForRecipeSelection;
    const loadingKey = substitutionKey(selectedDayForRecipeSelection, targetMealType);

    posthog?.capture(AnalyticsEvent.SubstitutionRequested, {
      type: 'cookbook',
      planned_day: selectedDayForRecipeSelection,
      meal_type: targetMealType,
    });

    // Add this slot to loading set
    beginSubstitution(loadingKey);

    try {
      // The response is the plan as it was *before* the substitution ran (the
      // work is queued), so the only thing worth taking from it is that the
      // request was accepted and the assignment is now pending.
      const updatedPlan = await substituteRecipe(
        mealPlan.id,
        selectedDayForRecipeSelection,
        recipe.id,
        undefined,
        undefined,
        undefined,
        targetMealType
      );
      if (updatedPlan) {
        markAssignmentPending(selectedDayForRecipeSelection, targetMealType);
      } else {
        // Request was rejected (e.g. usage limit) without throwing
        endSubstitution(loadingKey);
      }
    } catch (error) {
      console.error('Error substituting with user recipe:', error);
      // Only clear on error
      endSubstitution(loadingKey);
    } finally {
      // Reset selection state
      setSelectedDayForRecipeSelection('');
      setSelectedMealTypeForRecipeSelection('dinner');
    }
  };

  const handleRemixSubmission = (day: string) => {
    // The remix request already returned, so the assignment is pending on the
    // server; mark it locally and start watching for the result.
    beginSubstitution(day);
    markAssignmentPending(day);
  };


  const handleSubmitInstructions = async () => {
    if (!mealPlan || !selectedDayForSubstitution) return;

    setSubstitutionModalOpen(false);

    const targetMealType = selectedMealTypeForSubstitution;
    const loadingKey = substitutionKey(selectedDayForSubstitution, targetMealType);

    const hasGuidance = !!substitutionContext.trim() || substitutionReasons.length > 0;
    posthog?.capture(AnalyticsEvent.SubstitutionRequested, {
      type: hasGuidance ? 'guided' : 'reroll',
      planned_day: selectedDayForSubstitution,
      meal_type: targetMealType,
      reason_count: substitutionReasons.length,
      reasons: substitutionReasons,
      has_instructions: !!substitutionContext.trim(),
      modify_existing: modifyExisting,
    });

    // Promote tapped ingredient chips to standing avoids, if the user opted in.
    // Deliberately not awaited into the swap's try block: this is a preference
    // edit, and failing it must not stop the swap the user actually asked for.
    if (substitutionAlwaysAvoid) {
      const terms = selectedIngredientDisplays(substitutionReasons);
      if (terms.length > 0 && user) {
        void (async () => {
          try {
            const token = await user.getIdToken();
            const { added } = await addFoodsToAvoid(token, terms);
            if (added.length > 0) {
              notifications.show({
                title: 'Added to foods to avoid',
                message: `We'll keep ${added.join(', ')} out of future plans.`,
                color: 'green',
              });
            }
          } catch (error) {
            console.error('Failed to add foods to avoid:', error);
            notifications.show({
              title: "Couldn't save that preference",
              message:
                'The swap went through, but we could not update your foods to avoid. You can add it in preferences.',
              color: 'orange',
            });
          }
        })();
      }
    }

    // Add this slot to loading set (for sync mode visual feedback)
    beginSubstitution(loadingKey);

    try {
      // Pass the substitution context (instructions), modify flag, and reason chips to the API
      const updatedPlan = await substituteRecipe(
        mealPlan.id,
        selectedDayForSubstitution,
        undefined,
        substitutionContext.trim() || undefined,
        modifyExisting,
        substitutionReasons,
        targetMealType
      );
      // The response carries the plan as it was before the substitution was
      // queued, so the new recipe isn't in it yet - all we take from a success
      // is that the assignment is now pending on the server.
      if (updatedPlan) {
        markAssignmentPending(selectedDayForSubstitution, targetMealType);
      } else {
        // Request was rejected (e.g. usage limit) without throwing
        endSubstitution(loadingKey);
      }
    } catch (error) {
      console.error('Error substituting with instructions:', error);
      // Only clear on error
      endSubstitution(loadingKey);
    } finally {
      // Reset modal state
      setSelectedDayForSubstitution('');
      setSelectedMealTypeForSubstitution('dinner');
      setSubstitutionContext('');
      setSubstitutionReasons([]);
    }
  };

  const handleToggleSaveRecipe = async (recipeId: string) => {
    if (!mealPlan) return;

    const assignments = mealPlan.assignments.filter(a => {
      const recipe = getAssignmentRecipe(a, mealPlan);
      return recipe?.id === recipeId;
    });
    if (!assignments || assignments.length === 0) return;
    // Slot-aware keys: saving Monday's breakfast must not dim Monday's dinner
    const days = assignments.map(assignmentSubstitutionKey);

    const firstRecipe = getAssignmentRecipe(assignments[0], mealPlan);
    if (!firstRecipe) return;
    const wasOriginallySaved = firstRecipe.isSaved || false;
    
    // Optimistic update - immediately toggle the UI state
    setMealPlan(prevPlan => {
      if (!prevPlan) return prevPlan;

      const updatedAssignments = prevPlan.assignments.map((a) => {
        const recipe = getAssignmentRecipe(a, prevPlan);
        if (recipe?.id === recipeId && a.scaledRecipe) {
          return {
            ...a,
            scaledRecipe: {
              ...a.scaledRecipe,
              isSaved: !wasOriginallySaved
            }
          };
        }
        return a;
      });

      return { ...prevPlan, assignments: updatedAssignments };
    });
    
    // Add day(s) to saving set for subtle loading indicator
    setSavingDays(prev => new Set(Array.from(prev).concat(...days)));
    
    try {
      let success = false;
      if (wasOriginallySaved) {
        // Unsave the recipe
        success = await removeRecipeFromUserCookbook(recipeId);
        if (success) {
          console.log(`Recipe ${recipeId} removed from cookbook!`);
        }
      } else {
        // Save the recipe
        success = await saveRecipeToUserCookbook(recipeId);
        if (success) {
          console.log(`Recipe ${recipeId} saved to cookbook!`);
        }
      }
      
      // If the API call failed, revert the optimistic update
      if (!success) {
        setMealPlan(prevPlan => {
          if (!prevPlan) return prevPlan;

          const updatedAssignments = prevPlan.assignments.map(a => {
            if (a.scaledRecipe?.id === recipeId) {
              return {
                ...a,
                scaledRecipe: {
                  ...a.scaledRecipe,
                  isSaved: wasOriginallySaved // Revert to original state
                }
              };
            }
            return a;
          });

          return { ...prevPlan, assignments: updatedAssignments };
        });
      }
    } catch (error) {
      console.error('Failed to toggle recipe save state:', error);

      // Revert the optimistic update on error
      setMealPlan(prevPlan => {
        if (!prevPlan) return prevPlan;

        const updatedAssignments = prevPlan.assignments.map(a => {
          const recipe = getAssignmentRecipe(a, prevPlan);
          if (recipe?.id === recipeId && a.scaledRecipe) {
            return {
              ...a,
              scaledRecipe: {
                ...a.scaledRecipe,
                isSaved: wasOriginallySaved // Revert to original state
              }
            };
          }
          return a;
        });

        return { ...prevPlan, assignments: updatedAssignments };
      });
    } finally {
      // Remove day(s) from saving set when API call completes
      setSavingDays(prev => {
        const newSet = new Set(prev);
        days.forEach((day) => newSet.delete(day));
        return newSet;
      });
    }
  };

  const handleMealPlanFeedback = () => {
    if (!mealPlan) return;
    setFeedbackContext({
      type: 'meal_plan',
      mealPlanId: mealPlan.id,
      mealPlanDateRange: formatDateRange(mealPlan, true)
    });
    setTimeout(() => setFeedbackModalOpen(true), 0);
  };

  const handleAssignmentFeedback = (assignment: MealPlanAssignment) => {
    if (!mealPlan) return;
    const recipe = getAssignmentRecipe(assignment);
    if (!recipe) return;
    setFeedbackContext({
      type: 'assignment',
      mealPlanId: mealPlan.id,
      assignmentId: assignment.id,
      recipeName: recipe.name,
      mealPlanDateRange: formatDateRange(mealPlan, true)
    });
    setTimeout(() => setFeedbackModalOpen(true), 0);
  };

  const updateRecipeRating = (recipeId: string, rating: 'up' | 'down' | undefined) => {
    setMealPlan(prevPlan => {
      if (!prevPlan) return prevPlan;
      const updatedAssignments = prevPlan.assignments.map(a => {
        if (a.scaledRecipe?.id === recipeId) {
          return { ...a, scaledRecipe: { ...a.scaledRecipe, rating } };
        }
        return a;
      });
      return { ...prevPlan, assignments: updatedAssignments };
    });
  };

  const handleRateUp = async (assignment: MealPlanAssignment) => {
    if (!user || !mealPlan) return;
    const recipe = getAssignmentRecipe(assignment);
    if (!recipe || recipe.rating) return;

    setMenuOpen(prev => ({ ...prev, [assignment.plannedDay]: false }));

    // Optimistic update
    updateRecipeRating(recipe.id, 'up');
    setRatingDays(prev => new Set(prev).add(assignmentSubstitutionKey(assignment)));

    try {
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/rate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipeId: recipe.id,
          rating: 'up',
          mealPlanId: mealPlan.id,
          plannedDate: assignment.plannedDate,
        }),
      });
    } catch (error) {
      console.error('Error rating recipe:', error);
      updateRecipeRating(recipe.id, undefined);
    } finally {
      setRatingDays(prev => {
        const next = new Set(prev);
        next.delete(assignmentSubstitutionKey(assignment));
        return next;
      });
    }
  };

  const handleRateDown = (assignment: MealPlanAssignment) => {
    const recipe = getAssignmentRecipe(assignment);
    if (recipe?.rating) return;
    setMenuOpen(prev => ({ ...prev, [assignment.plannedDay]: false }));
    setRatingAssignment(assignment);
    setTimeout(() => setRatingModalOpen(true), 0);
  };

  const handleRateDownSubmit = async (selectedTags: string[], dietaryViolations: string[]) => {
    if (!user || !mealPlan || !ratingAssignment) return;
    const recipe = getAssignmentRecipe(ratingAssignment);
    if (!recipe) return;

    const capturedAssignment = ratingAssignment;
    setRatingModalOpen(false);
    setRatingAssignment(null);

    // Optimistic update
    updateRecipeRating(recipe.id, 'down');
    setRatingDays(prev => new Set(prev).add(assignmentSubstitutionKey(capturedAssignment)));

    try {
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/rate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipeId: recipe.id,
          rating: 'down',
          feedbackTags: selectedTags,
          mealPlanId: mealPlan.id,
          plannedDate: capturedAssignment.plannedDate,
          dietaryViolations: dietaryViolations.length > 0 ? dietaryViolations : undefined,
        }),
      });
    } catch (error) {
      console.error('Error rating recipe:', error);
      updateRecipeRating(recipe.id, undefined);
    } finally {
      setRatingDays(prev => {
        const next = new Set(prev);
        next.delete(assignmentSubstitutionKey(capturedAssignment));
        return next;
      });
    }
  };

  const handleUndoRating = async (assignment: MealPlanAssignment) => {
    if (!user || !mealPlan) return;
    const recipe = getAssignmentRecipe(assignment);
    if (!recipe || !recipe.rating) return;

    const previousRating = recipe.rating;
    updateRecipeRating(recipe.id, undefined);
    setRatingDays(prev => new Set(prev).add(assignmentSubstitutionKey(assignment)));

    try {
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/rate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipeId: recipe.id,
          rating: 'none',
        }),
      });
    } catch (error) {
      console.error('Error undoing rating:', error);
      updateRecipeRating(recipe.id, previousRating);
    } finally {
      setRatingDays(prev => {
        const next = new Set(prev);
        next.delete(assignmentSubstitutionKey(assignment));
        return next;
      });
    }
  };

  const handleToggleGroceryItem = async (index: number) => {
    if (!user) return;

    const item = groceryList[index];
    if (!item) return;

    // Close any open preference input for this item
    setExpandedPreferenceItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(item.itemName);
      return newSet;
    });

    // Optimistic update - immediately toggle the UI
    const newValue = !item.alreadyHave;
    const seq = ++groceryMutationSeqRef.current;
    setGroceryList(prev =>
      prev.map((it, i) =>
        i === index ? { ...it, alreadyHave: newValue } : it
      )
    );

    // Revert only this item; a full-snapshot rollback would also undo other
    // toggles made while this request was in flight
    const revertThisItem = () =>
      setGroceryList(prev =>
        prev.map(it =>
          it.itemName === item.itemName ? { ...it, alreadyHave: !newValue } : it
        )
      );

    try {
      const token = await user.getIdToken();
      const result = await toggleGroceryListItems(effectivePlanId, [item.itemName], token);

      if (result.success && result.groceryList) {
        // Sync to the server's list only if no other grocery mutation started
        // while this request was in flight; a stale full-list snapshot would
        // clobber newer optimistic toggles and make items visibly bounce.
        if (groceryMutationSeqRef.current === seq) {
          setGroceryList(current => {
            const serverState = result.groceryList!;
            const matchesOptimistic = serverState.length === current.length &&
              serverState.every((s, i) =>
                s.itemName === current[i].itemName && s.alreadyHave === current[i].alreadyHave
              );
            // Skip the no-op update to avoid a full list re-render
            return matchesOptimistic ? current : serverState;
          });
        }
      } else {
        // Rollback on failure
        revertThisItem();
      }
    } catch (error) {
      console.error('Error toggling grocery item:', error);
      // Rollback the optimistic update
      revertThisItem();
      // Could show an error message to the user here
    }
  };

  const categorizeGroceryItem = (item: GroceryItem): string => {
    // Use AI-generated category if available, otherwise fall back to 'other'
    return item.category || 'other';
  };

  const getCategorizedItems = () => {
    const sortItems = (items: GroceryItem[]) => 
      items.sort((a, b) => a.itemName.toLowerCase().localeCompare(b.itemName.toLowerCase()));
    
    const categorized = {
      produce: sortItems(groceryList.filter(item => categorizeGroceryItem(item) === 'produce')),
      meat: sortItems(groceryList.filter(item => categorizeGroceryItem(item) === 'meat')),
      dairy: sortItems(groceryList.filter(item => categorizeGroceryItem(item) === 'dairy')),
      pantry_dry_goods: sortItems(groceryList.filter(item => categorizeGroceryItem(item) === 'pantry_dry_goods')),
      spices_seasonings: sortItems(groceryList.filter(item => categorizeGroceryItem(item) === 'spices_seasonings')),
      frozen: sortItems(groceryList.filter(item => categorizeGroceryItem(item) === 'frozen')),
      bakery: sortItems(groceryList.filter(item => categorizeGroceryItem(item) === 'bakery')),
      beverages: sortItems(groceryList.filter(item => categorizeGroceryItem(item) === 'beverages')),
      other: sortItems(groceryList.filter(item => categorizeGroceryItem(item) === 'other')),
    };
    
    return categorized;
  };

  const getCategoryDisplayName = (category: string): string => {
    const displayNames: { [key: string]: string } = {
      all: 'All',
      produce: 'Produce',
      meat: 'Meat',
      dairy: 'Dairy',
      pantry_dry_goods: 'Pantry',
      spices_seasonings: 'Spices',
      frozen: 'Frozen',
      bakery: 'Bakery',
      beverages: 'Beverages',
      other: 'Other',
    };
    return displayNames[category] || category;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'orange';
      case 'draft': return 'indigo';
      case 'completed': return 'blue';
      default: return 'gray';
    }
  };

  const isKrogerConnected = () => {
    return userProfile?.krogerConnected || false;
  };

  const handleAddToKrogerCart = async () => {
    if (!isKrogerConnected() || !user) return;

    // Check if user has selected a store location
    if (!userProfile?.krogerInfo?.location) {
      setLocationModalOpen(true);
      return;
    }
    
    // User has location, proceed with cart addition
    proceedWithCartAddition();
  };

  const handleCheckOffAddedIngredients = async () => {
    if (!cartResults?.addedItems || !user) return;

    // Get ingredient names that were successfully added to cart
    const addedIngredientNames = cartResults.addedItems.map(item => item.itemName);

    // Optimistic update - immediately mark items as "already have"
    const previousState = [...groceryList];
    setGroceryList(prev => 
      prev.map(item => {
        if (addedIngredientNames.includes(item.itemName)) {
          return { ...item, alreadyHave: true };
        }
        return item;
      })
    );

    // Close the modal immediately for better UX
    setCartModalOpen(false);

    try {
      // Use the toggle API to persist the changes
      const token = await user.getIdToken();
      const result = await toggleGroceryListItems(effectivePlanId, addedIngredientNames, token);
      
      if (result.success && result.groceryList) {
        // Update with server response (in case of any differences)
        setGroceryList(result.groceryList);
      } else {
        // Rollback on failure
        setGroceryList(previousState);
      }
    } catch (error) {
      console.error('Error updating grocery list:', error);
      // Rollback the optimistic update
      setGroceryList(previousState);
      // Could show an error message to the user here
    }
  };

  const handleLocationSelected = async (_locationId: string, _fulfillmentPreference: 'pickup' | 'delivery') => {
    // Refresh user profile to get updated location
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const profile = data.user?.familyProfile;
        if (profile) {
          setUserProfile(profile);
          
          // Now that we have a location, proceed with adding to cart
          proceedWithCartAddition();
        }
      }
    } catch (error) {
      console.error('Error refreshing user profile after location selection:', error);
    }
  };

  const proceedWithCartAddition = async () => {
    if (!user || !mealPlan) return;
    
    setAddingToCart(true);
    setCartResults(null);
    setCartProgress({
      step: 'pending',
      message: 'Starting...',
      totalItems: groceryList.filter(item => !item.alreadyHave).length
    });
    
    try {
      const token = await user.getIdToken();
      
      // Start the cart addition process and get initial status
      const initialStatus = await addMealPlanToCart(effectivePlanId, token);
      
      // Initialize progress with the returned status
      setCartProgress({
        step: initialStatus.status,
        message: 'Preparing cart...',
        itemsProcessed: initialStatus.productSearchProgress.current,
        totalItems: initialStatus.productSearchProgress.total
      });
      
      // Start realtime tracking for status updates
      setCartTrackingActive(true);

    } catch (error) {
      console.error('Failed to start cart addition:', error);
      const itemsToProcess = groceryList.filter(item => !item.alreadyHave);
      setCartResults({
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        failedItems: itemsToProcess.map(item => ({
          itemName: item.itemName,
          reason: 'Failed to start cart addition process'
        }))
      });
      setCartProgress({
        step: 'failed',
        message: 'Error occurred during processing',
        totalItems: itemsToProcess.length
      });
      setAddingToCart(false);
    }
  };

  // Product preference handlers
  const handleToggleProductPreference = (itemName: string) => {
    setExpandedPreferenceItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemName)) {
        newSet.delete(itemName);
      } else {
        newSet.add(itemName);
      }
      return newSet;
    });
  };

  const handleSaveProductPreference = async (itemName: string, preference: string, scope: 'meal_plan' | 'ingredient' | 'category' | 'global' = 'meal_plan') => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      
      // Update local state optimistically
      setGroceryList(prev => prev.map(item => 
        item.itemName === itemName 
          ? { ...item, productPreference: preference.trim() || undefined, productPreferenceScope: scope === 'meal_plan' ? undefined : scope }
          : item
      ));
      
      // Remove from expanded items
      setExpandedPreferenceItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemName);
        return newSet;
      });

      if (scope === 'meal_plan') {
        // Original behavior - save to meal plan only
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/meals/plans/${effectivePlanId}/grocery-preferences`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            itemName,
            productPreference: preference.trim() || null,
            productPreferenceScope: null
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to save product preference');
        }
        
        const result = await response.json();
        if (result.success && result.groceryList) {
          setGroceryList(result.groceryList);
        }
      } else {
        // Handle persistent preferences
        const trimmedPreference = preference.trim();
        
        if (trimmedPreference === '') {
          // Delete the preference if empty
          // Get the category from the current grocery item for proper scope handling
          const currentGroceryItem = groceryList.find(item => item.itemName === itemName);
          const itemCategory = currentGroceryItem?.category;
          
          let getUrl = '';
          if (scope === 'category') {
            getUrl = `${process.env.REACT_APP_API_URL}/api/product-preferences?scope=category&category=${encodeURIComponent(itemCategory || '')}`;
          } else if (scope === 'global') {
            getUrl = `${process.env.REACT_APP_API_URL}/api/product-preferences?scope=global`;
          } else {
            getUrl = `${process.env.REACT_APP_API_URL}/api/product-preferences/${encodeURIComponent(itemName)}`;
          }
          
          const getResponse = await fetch(getUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (getResponse.ok) {
            const result = await getResponse.json();
            const existingPreference = scope === 'ingredient' ? result.preference : 
                                     (result.preferences && result.preferences.length > 0 ? result.preferences[0] : null);
            
            if (existingPreference) {
              // Delete the existing preference
              await fetch(`${process.env.REACT_APP_API_URL}/api/product-preferences/${existingPreference.id}`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });
            }
          }
        } else {
          // Save as persistent preference
          // Get the category from the current grocery item
          const currentGroceryItem = groceryList.find(item => item.itemName === itemName);
          const itemCategory = currentGroceryItem?.category;
          
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/product-preferences/${encodeURIComponent(itemName)}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              customPreference: trimmedPreference,
              scope: scope === 'ingredient' ? 'ingredient' : scope === 'category' ? 'category' : 'global',
              category: scope === 'category' ? itemCategory : undefined,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to save product preference');
          }
        }
        
        // Also update the meal plan preference
        const mealPlanResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/meals/plans/${effectivePlanId}/grocery-preferences`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            itemName,
            productPreference: preference.trim() || null,
            productPreferenceScope: scope
          }),
        });

        if (mealPlanResponse.ok) {
          const result = await mealPlanResponse.json();
          if (result.success && result.groceryList) {
            setGroceryList(result.groceryList);
          }
        }
      }
    } catch (error) {
      console.error('Error saving product preference:', error);
      // Revert optimistic update
      setGroceryList(prev => {
        const currentItem = groceryList.find(item => item.itemName === itemName);
        return prev.map(item => 
          item.itemName === itemName 
            ? { ...item, productPreference: currentItem?.productPreference, productPreferenceScope: currentItem?.productPreferenceScope }
            : item
        );
      });
      
      notifications.show({
        title: 'Failed to save preference',
        message: 'Please try again',
        color: 'red',
      });
    }
  };

  // Check if there's a valid cached Instacart link
  const hasValidInstacartCache = useMemo(() => {
    if (!mealPlan?.instacartShoppingListCache?.url) return false;
    
    // Check if cache is expired
    const isExpired = new Date(mealPlan.instacartShoppingListCache.expiresAt) <= new Date();
    return !isExpired;
  }, [mealPlan?.instacartShoppingListCache]);

  const handleAddToInstacart = async () => {
    if (!user) return;

    // If we have a valid cached link, just open it
    if (hasValidInstacartCache && mealPlan?.instacartShoppingListCache?.url) {
      window.open(mealPlan.instacartShoppingListCache.url, '_blank');
      return;
    }

    // Open a progress indicator window immediately to avoid popup blocker
    const newWindow = window.open('/instacart-redirect.html', '_blank');
    
    setAddingToInstacart(true);
    
    try {
      const result = await createInstacartShoppingList(effectivePlanId, await user.getIdToken());
      
      // Update local meal plan state with the new Instacart cache data
      if (mealPlan) {
        setMealPlan({
          ...mealPlan,
          instacartShoppingListCache: {
            url: result.instacartUrl,
            expiresAt: result.expiresAt,
            createdAt: new Date().toISOString(),
            groceryListHash: '' // Backend manages the hash
          }
        });
      }
      
      // Navigate the already-opened window to Instacart
      if (newWindow) {
        newWindow.location.href = result.instacartUrl;
      } else {
        // Fallback if window opening failed
        window.open(result.instacartUrl, '_blank');
      }
      
    } catch (error) {
      console.error('Error creating Instacart shopping list:', error);
      
      // Close the placeholder window on error
      if (newWindow) {
        newWindow.close();
      }
      
      notifications.show({
        title: 'Error',
        message: 'Failed to create Instacart shopping list. Please try again.',
        color: 'red'
      });
    } finally {
      setAddingToInstacart(false);
    }
  };

  // Handle printing shopping list
  const handlePrintShoppingList = () => {
    printShoppingList(mealPlan!, groceryList);
  };

  // Handle saving additional items
  const handleSaveAdditionalItems = async (items: string[], productMatchingInstructions?: string, productPreferenceOverrides?: {
    brandPreference?: "store" | "name" | "no_preference";
    organicPreference?: "prefer_organic" | "no_preference";
    pricePreference?: "value" | "mid_range" | "premium" | "no_preference";
    specificBrands?: string[];
    avoidBrands?: string[];
  }) => {
    if (!mealPlan || !user) return;

    setSavingAdditionalItems(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/meals/plans/${effectivePlanId}/shopping-customization`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({ additionalItems: items, productMatchingInstructions, productPreferenceOverrides })
      });

      if (!response.ok) {
        throw new Error('Failed to save additional items');
      }

      // Update local state
      setMealPlan({ 
        ...mealPlan, 
        additionalItems: items,
        productMatchingInstructions: productMatchingInstructions,
        productPreferenceOverrides: productPreferenceOverrides
      });
      setAdditionalItemsDrawerOpen(false);
      

    } catch (error) {
      console.error('Error saving shopping customizations:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save customizations. Please try again.',
        color: 'red'
      });
    } finally {
      setSavingAdditionalItems(false);
    }
  };

  // Delete assignment with undo functionality - optimistic removal
  const handleDeleteAssignment = async (assignment: MealPlanAssignment) => {
    if (!mealPlan) return;

    const recipe = getAssignmentRecipe(assignment, mealPlan);
    const recipeName = recipe?.name ?? assignment.customMeal?.name ?? assignment.plannedDay;
    const mealType = getAssignmentMealType(assignment);
    const removeKey = deletionKey(assignment);

    // Deletion was the one plan mutation with no event at all
    posthog?.capture(AnalyticsEvent.AssignmentRemoved, {
      meal_plan_id: mealPlan.id,
      planned_day: assignment.plannedDay,
      meal_type: mealType,
      source: assignment.source,
    });

    // Optimistically remove assignment from UI immediately
    setDeletedAssignments(prev => new Set(prev).add(removeKey));

    const notificationId = `delete-${removeKey}`;

    // Create undo button component with loading state
    const UndoButton = () => {
      const [isUndoing, setIsUndoing] = useState(false);

      return (
        <Button
          size="xs"
          variant="subtle"
          color="orange"
          loading={isUndoing}
          onClick={async () => {
            setIsUndoing(true);
            try {
              await handleRestoreDay(assignment.plannedDate, mealType);
            } finally {
              setIsUndoing(false);
            }
          }}
        >
          Undo
        </Button>
      );
    };

    // Show undo notification immediately
    notifications.show({
      id: notificationId,
      title: mealType === 'dinner' ? 'Day removed from meal plan' : `${mealType === 'breakfast' ? 'Breakfast' : 'Lunch'} removed from meal plan`,
      message: (
        <Group>
          <Text size="sm">{`${assignment.plannedDay} (${recipeName}) has been removed`}</Text>
          <UndoButton />
        </Group>
      ),
      color: 'orange',
      autoClose: 5000,
      withCloseButton: true,
    });

    // Perform actual API call in background
    try {
      const updatedMealPlan = await deleteMealPlanAssignment(mealPlan.id, assignment.plannedDate, mealType);
      if (updatedMealPlan) {
        setMealPlan(updatedMealPlan);
      } else {
        // If API call failed, restore the assignment
        setDeletedAssignments(prev => {
          const newSet = new Set(prev);
          newSet.delete(removeKey);
          return newSet;
        });
        notifications.hide(notificationId);
        notifications.show({
          title: 'Error',
          message: 'Failed to remove day from meal plan. Please try again.',
          color: 'red',
        });
      }
    } catch (error) {
      // If API call errored, restore the assignment
      setDeletedAssignments(prev => {
        const newSet = new Set(prev);
        newSet.delete(removeKey);
        return newSet;
      });
      notifications.hide(notificationId);
      notifications.show({
        title: 'Error',
        message: 'Failed to remove day from meal plan. Please try again.',
        color: 'red',
      });
    }
  };

  // Restore a soft-deleted assignment, invoked from the undo toast or from a
  // placeholder card. Slot-aware: dinner keys are the bare date (what the
  // placeholder cards use); other slots use date#mealType, matching
  // deletionKey. Returns whether the assignment is back in the plan.
  const handleRestoreDay = async (plannedDate: string, mealType: MealType = 'dinner'): Promise<boolean> => {
    if (!mealPlan) return false;

    const restoreKey = mealType === 'dinner' ? plannedDate : `${plannedDate}#${mealType}`;
    setRestoringDays(prev => new Set(prev).add(restoreKey));
    try {
      const updatedMealPlan = await undoDeleteMealPlanAssignment(mealPlan.id, plannedDate, mealType);
      if (updatedMealPlan) {
        setMealPlan(updatedMealPlan);
        // Remove from deleted assignments set so it reappears
        setDeletedAssignments(prev => {
          const newSet = new Set(prev);
          newSet.delete(restoreKey);
          return newSet;
        });
        notifications.hide(`delete-${restoreKey}`);
        return true;
      }
      notifications.show({
        title: 'Error',
        message: 'Failed to restore day to meal plan',
        color: 'red',
      });
      return false;
    } finally {
      setRestoringDays(prev => {
        const newSet = new Set(prev);
        newSet.delete(restoreKey);
        return newSet;
      });
    }
  };

  // Bring the old meal back, then immediately open the swap flow on it so the
  // placeholder's "Pick a different meal" lands the user in the usual picker.
  const handleRestoreAndPickDifferent = async (day: DeletedDaySummary) => {
    const mealType = day.mealType ?? 'dinner';
    const restored = await handleRestoreDay(day.plannedDate, mealType);
    if (restored) {
      handleSubstituteWithInstructions(day.plannedDay, mealType);
    }
  };

  // Lazily fetch remembered meals the first time the pencil-in sheet opens
  useEffect(() => {
    if (customMealTarget?.mode === 'add' && savedPenciledMeals === null) {
      getPenciledMeals().then(setSavedPenciledMeals);
    }
  }, [customMealTarget, savedPenciledMeals, getPenciledMeals]);

  // "Pencil in your own meal" from the swap drawer: open the pencil-in sheet
  // in replace mode. Nothing changes until the user submits — cancel is a
  // no-op, and the swap itself happens atomically server-side (same
  // assignment id).
  const handlePencilInReplace = (assignment: MealPlanAssignment) => {
    setCustomMealTarget({
      mode: 'add',
      plannedDate: assignment.plannedDate,
      plannedDay: assignment.plannedDay,
      mealType: getAssignmentMealType(assignment),
      replacingName: mealPlan ? getAssignmentRecipe(assignment, mealPlan)?.name : undefined,
    });
  };

  const handleSubmitCustomMeal = async (meal: CustomMeal, meta: { usedMemory: boolean }) => {
    if (!mealPlan || !customMealTarget) return;

    setSavingCustomMeal(true);
    try {
      const updated =
        customMealTarget.mode === 'add'
          ? await addCustomMealAssignment(
              mealPlan.id,
              customMealTarget.plannedDate,
              meal,
              !!customMealTarget.replacingName,
              customMealTarget.mealType
            )
          : await updateCustomMealAssignment(mealPlan.id, customMealTarget.assignment.id, meal);

      if (!updated) {
        notifications.show({
          title: 'Error',
          message: 'Failed to save your meal. Please try again.',
          color: 'red',
        });
        return;
      }

      setMealPlan(updated);
      if (customMealTarget.mode === 'add') {
        // Adding onto a removed slot replaces its tombstone server-side; clear
        // the local optimistic-delete marker so the new card isn't filtered out
        const markerKey = deletedDayKey({
          plannedDate: customMealTarget.plannedDate,
          mealType: customMealTarget.mealType,
        });
        setDeletedAssignments(prev => {
          const newSet = new Set(prev);
          newSet.delete(markerKey);
          return newSet;
        });
        notifications.hide(`delete-${markerKey}`);
      }
      posthog?.capture(
        customMealTarget.mode === 'add'
          ? AnalyticsEvent.CustomMealAdded
          : AnalyticsEvent.CustomMealEdited,
        {
          meal_plan_id: mealPlan.id,
          meal_type:
            customMealTarget.mode === 'add'
              ? customMealTarget.mealType
              : getAssignmentMealType(customMealTarget.assignment),
          ingredient_count: meal.ingredients.length,
          has_note: !!meal.note,
          replaced_recipe: customMealTarget.mode === 'add' && !!customMealTarget.replacingName,
          used_memory: meta.usedMemory,
        }
      );
      // Mirror the server-side memory upsert locally so the next open shows
      // this meal without a refetch. mealType mirrors the server's
      // latest-use-wins slot stamp.
      const memoryMealType =
        customMealTarget.mode === 'add'
          ? customMealTarget.mealType
          : getAssignmentMealType(customMealTarget.assignment);
      setSavedPenciledMeals(prev => {
        // Memories are per name AND slot (mirrors the server's doc keying):
        // lunch "Takeout" must not replace the dinner "Takeout" fill
        const memoryKey = (name: string, slot: string) => `${name.trim().toLowerCase()}#${slot}`;
        const key = memoryKey(meal.name, memoryMealType);
        const existing = (prev ?? []).find(
          m => memoryKey(m.name, m.mealType ?? 'dinner') === key
        );
        const updated: PenciledMealMemory = {
          ...meal,
          useCount: (existing?.useCount ?? 0) + 1,
          lastUsedAt: new Date().toISOString(),
          mealType: memoryMealType,
        };
        return [
          updated,
          ...(prev ?? []).filter(m => memoryKey(m.name, m.mealType ?? 'dinner') !== key),
        ];
      });
      setCustomMealTarget(null);
    } finally {
      setSavingCustomMeal(false);
    }
  };

  const handleTooltipClick = (assignmentDay: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Toggle tooltip - close if already open, otherwise open this one
    setOpenTooltip(prev => prev === assignmentDay ? null : assignmentDay);
  };


  const sortedAssignments = useMemo(() =>
    mealPlan?.assignments
      // Soft-deleted assignments are keyed per slot (bare date for dinner),
      // so deleting one slot never hides the date's other meals
      .filter(assignment => !deletedAssignments.has(deletionKey(assignment)))
      .sort((a, b) =>
        new Date(a.plannedDate).getTime() - new Date(b.plannedDate).getTime()
      ) || []
  , [mealPlan?.assignments, deletedAssignments]);

  // The grid shows one meal type at a time; a segmented control switches the
  // whole plan between slots. Dinner is the primary (and only draggable) view.
  const dinnerAssignments = useMemo(
    () => sortedAssignments.filter(a => getAssignmentMealType(a) === 'dinner'),
    [sortedAssignments]
  );
  const hasBreakfastSlots = useMemo(
    () => sortedAssignments.some(a => getAssignmentMealType(a) === 'breakfast'),
    [sortedAssignments]
  );
  const hasLunchSlots = useMemo(
    () => sortedAssignments.some(a => getAssignmentMealType(a) === 'lunch'),
    [sortedAssignments]
  );
  const mealTabOptions = useMemo(() => {
    const options: { label: string; value: MealType | 'cooks' }[] = [];
    if (hasBreakfastSlots) options.push({ label: 'Breakfasts', value: 'breakfast' });
    if (hasLunchSlots) options.push({ label: 'Lunches', value: 'lunch' });
    options.push({ label: 'Dinners', value: 'dinner' });
    // The cook schedule: the plan regrouped by cooking sessions. Only
    // meaningful (and only offered) when the plan spans multiple meals.
    if (options.length > 1) options.push({ label: 'Cook plan', value: 'cooks' });
    return options;
  }, [hasBreakfastSlots, hasLunchSlots]);
  // Edge-fade hints for the horizontally scrollable tab row: an attribute
  // per edge that still has tabs past it, driving the CSS mask. Without
  // them a narrow viewport slices a backgroundless label mid-word, which
  // reads as accidental truncation rather than "scroll for more".
  const mealTabsWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrap = mealTabsWrapRef.current;
    if (!wrap) return;
    const list = wrap.querySelector<HTMLElement>('.mantine-Tabs-list');
    if (!list) return;
    const update = () => {
      list.toggleAttribute(
        'data-fade-right',
        list.scrollLeft + list.clientWidth < list.scrollWidth - 1
      );
      list.toggleAttribute('data-fade-left', list.scrollLeft > 1);
    };
    update();
    list.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      list.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [mealTabOptions]);

  // Fall back to dinner if the selected slot disappears (e.g. last breakfast
  // deleted while its tab is active)
  const effectiveMealTab: MealType | 'cooks' =
    (activeMealTab === 'breakfast' && hasBreakfastSlots) ||
    (activeMealTab === 'lunch' && hasLunchSlots) ||
    (activeMealTab === 'cooks' && (hasBreakfastSlots || hasLunchSlots))
      ? activeMealTab
      : 'dinner';
  // Every tab lays out over the same day spine so a given day occupies the
  // same grid position in all tabs; days without the selected meal render a
  // placeholder cell instead of collapsing (e.g. Monday in a leftovers-lunch
  // plan, which has no lunch to draw from yet)
  const planDaySpine = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of sortedAssignments) {
      if (!seen.has(a.plannedDate)) seen.set(a.plannedDate, a.plannedDay);
    }
    return Array.from(seen.entries())
      .map(([date, day]) => ({ date, day }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [sortedAssignments]);
  const slotTabCells = useMemo(
    () =>
      planDaySpine.map(({ date, day }) => ({
        date,
        day,
        assignment:
          sortedAssignments.find(
            a => a.plannedDate === date && getAssignmentMealType(a) === effectiveMealTab
          ) ?? null,
      })),
    [planDaySpine, sortedAssignments, effectiveMealTab]
  );
  // The active slot tab's assignments in day order, for drag reordering
  const slotTabAssignments = useMemo(
    () => slotTabCells.flatMap(c => (c.assignment ? [c.assignment] : [])),
    [slotTabCells]
  );
  // Rotation breakfasts/lunches reorder like dinners do. Leftover-mode
  // lunches don't: their order is derived state ("the most recent cooked
  // dinner before this day"), re-computed whenever dinners move, so a drag
  // would re-derive right back to a no-op. (isOwner && !planIsPast is
  // canModifyPlan, which is declared after the early returns below.)
  const slotDragEnabled =
    isOwner &&
    !planIsPast &&
    (effectiveMealTab === 'breakfast' ||
      (effectiveMealTab === 'lunch' &&
        mealPlan?.preferences.additionalMeals?.lunch?.mode !== 'leftovers'));

  // Selecting a tab that's partially scrolled out of the row brings it
  // fully into view ('nearest' keeps the page's vertical position still)
  useEffect(() => {
    mealTabsWrapRef.current
      ?.querySelector<HTMLElement>('.mantine-Tabs-tab[data-active]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [effectiveMealTab, mealTabOptions]);

  // Fade the panel in on tab switch without remounting it: a keyed CSS
  // animation rebuilt every card (the lag), so animate imperatively instead
  // and let React reuse the existing DOM. Opacity only; transforms would
  // give positioned descendants a surprise containing block mid-animation.
  const mealTabPanelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    mealTabPanelRef.current?.animate?.(
      [{ opacity: 0 }, { opacity: 1 }],
      { duration: 160, easing: 'ease-out' }
    );
  }, [effectiveMealTab]);
  // Same gate the per-card modify actions use: no slot fills while the plan
  // is activating or its grocery list is being generated
  const slotAddDisabled =
    loadingActivation ||
    mealPlan?.groceryListStatus?.status === 'pending' ||
    mealPlan?.groceryListStatus?.status === 'generating';

  const handleMealTabChange = useCallback((value: string) => {
    setActiveMealTab(value as MealType);
    // Per-day UI state is keyed by plannedDay, which repeats across tabs
    setMenuOpen({});
    setServingsInputMode(null);
    setOpenTooltip(null);
  }, [setActiveMealTab]);
  // Date#slot keys the server has confirmed as soft-deleted (restorable). A
  // key missing from this set but present in deletedAssignments is a delete
  // still in flight, so its placeholder renders with restore actions disabled.
  const confirmedDeletedKeys = useMemo(() =>
    new Set((mealPlan?.deletedDays ?? []).map(deletedDayKey))
  , [mealPlan?.deletedDays]);

  // Removed meals shown as placeholder cards: the server's soft-deleted
  // tombstones plus any optimistically hidden ones it hasn't confirmed yet.
  // Matching is by the slot-qualified key: deleting a dinner must not turn
  // the date's breakfast and lunch into phantom placeholders.
  const deletedDayCells = useMemo((): DeletedDaySummary[] => {
    if (!mealPlan) return [];
    const optimistic = mealPlan.assignments
      .filter(a => deletedAssignments.has(deletionKey(a)) && !confirmedDeletedKeys.has(deletionKey(a)))
      .map(a => {
        const mealType = getAssignmentMealType(a);
        return {
          plannedDate: a.plannedDate,
          plannedDay: a.plannedDay,
          ...(mealType !== 'dinner' ? { mealType } : {}),
          recipeName: getAssignmentRecipe(a, mealPlan)?.name ?? a.customMeal?.name,
          source: a.source,
        };
      });
    return [...(mealPlan.deletedDays ?? []), ...optimistic];
  }, [mealPlan, deletedAssignments, confirmedDeletedKeys]);

  // Live meal cards and removed-day placeholders interleaved in date order,
  // so a removed day keeps its position in the week instead of leaving a gap.
  // Dinner tombstones only: breakfast/lunch placeholders live in their slot
  // tabs' grids.
  const planGridCells = useMemo(() => {
    const cells = [
      ...dinnerAssignments.map(a => ({ kind: 'meal' as const, assignment: a, plannedDate: a.plannedDate })),
      ...deletedDayCells
        .filter(d => (d.mealType ?? 'dinner') === 'dinner')
        .map(d => ({ kind: 'deleted' as const, day: d, plannedDate: d.plannedDate })),
    ];
    return cells.sort((a, b) => a.plannedDate.localeCompare(b.plannedDate));
  }, [dinnerAssignments, deletedDayCells]);

  // The draft tour anchors to the first recipe card; custom meal cards carry
  // none of its targets
  const firstRecipeAssignmentId = useMemo(
    () => sortedAssignments.find(a => a.source !== 'custom_meal')?.id,
    [sortedAssignments]
  );


  // Meal prep validation for drag-and-drop
  // Drag handlers for recipe reordering
  const handleAssignmentDragStart = useCallback((event: DragStartEvent) => {
    setActiveAssignmentId(event.active.id.toString());
  }, []);

  // The PATCH leaves cookedOnDate alone when the field is missing, and JSON
  // drops undefined - so "no cook-ahead" has to travel as an explicit null,
  // or an undo of a cook-day change could never clear the date it had set.
  const wireAssignments = (assignments: MealPlanAssignment[]) =>
    assignments.map(a => ({ ...a, cookedOnDate: a.cookedOnDate ?? null }));

  // Shared tail of every reorder: optimistic apply, persist, undo toast,
  // rollback on failure. Reordering doesn't touch lastModifiedAt since it
  // doesn't affect the grocery list.
  const persistReorder = useCallback(async (
    updatedAssignments: MealPlanAssignment[],
    message: string = 'Meal order updated'
  ) => {
    if (!mealPlan) return;
    const previousMealPlan = mealPlan;
    const updatedPlan = { ...mealPlan, assignments: updatedAssignments };
    setMealPlan(updatedPlan);

    try {
      // The hook swallows request errors and reports them as false, so the
      // rollback below only ever runs off this check
      const saved = await updateMealPlan(mealPlan.id, { assignments: wireAssignments(updatedAssignments) });
      if (!saved) throw new Error('Failed to update meal plan');

      const notificationId = 'recipe-reorder-undo';

      const UndoButton = () => {
        const [isUndoing, setIsUndoing] = useState(false);

        return (
          <Button
            size="xs"
            variant="subtle"
            color="blue"
            loading={isUndoing}
            onClick={async () => {
              setIsUndoing(true);
              notifications.hide(notificationId);

              // Restore previous state in UI
              setMealPlan(previousMealPlan);

              // Restore in backend
              try {
                const restored = await updateMealPlan(mealPlan.id, { assignments: wireAssignments(previousMealPlan.assignments) });
                if (!restored) throw new Error('Failed to restore');
              } catch (error) {
                // If undo fails, revert to the updated plan
                setMealPlan(updatedPlan);
                notifications.show({
                  title: 'Undo failed',
                  message: 'Failed to restore recipe order',
                  color: 'red',
                });
              } finally {
                setIsUndoing(false);
              }
            }}
          >
            Undo
          </Button>
        );
      };

      // Show undo notification
      notifications.show({
        id: notificationId,
        message: (
          <Group>
            <Text size="sm">{message}</Text>
            <UndoButton />
          </Group>
        ),
        color: 'blue',
        autoClose: 5000,
        withCloseButton: true,
      });
    } catch (error) {
      // Rollback on error
      setMealPlan(mealPlan);
      notifications.show({
        title: 'Move failed',
        message: 'Failed to move recipe. Please try again.',
        color: 'red',
      });
    }
  }, [mealPlan, updateMealPlan, setMealPlan]);

  const handleAssignmentDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveAssignmentId(null);

    if (!over || active.id === over.id || !mealPlan) return;

    // Reordering is slot-constrained: only dinner cards are draggable, and a
    // drag swaps dinners between dates. Breakfast/lunch rows stay put.
    const oldIndex = dinnerAssignments.findIndex(a => a.id === active.id);
    const newIndex = dinnerAssignments.findIndex(a => a.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder the assignments array first to simulate the move
    const reorderedAssignments = arrayMove(dinnerAssignments, oldIndex, newIndex);

    // Build mapping of cook day assignments: old date -> new date
    const cookDayDateMapping = new Map<string, string>();
    reorderedAssignments.forEach((assignment, index) => {
      if (assignment.isCookDay) {
        const oldDate = assignment.plannedDate;
        const newDate = dinnerAssignments[index].plannedDate;
        cookDayDateMapping.set(oldDate, newDate);
      }
    });

    // Update plannedDate and plannedDay for each assignment based on its new position
    // Also update preparedOnDate for prep days to reflect cook day's new date
    const updatedDinners = reorderedAssignments.map((assignment, index) => {
      const newPlannedDate = dinnerAssignments[index].plannedDate;
      const newPlannedDay = dinnerAssignments[index].plannedDay;

      const updated = {
        ...assignment,
        plannedDate: newPlannedDate,
        plannedDay: newPlannedDay,
      };

      // If this is a prep day, update its preparedOnDate to the cook day's new date
      if (assignment.source === 'assignment_reference' && assignment.preparedOnDate) {
        const newCookDayDate = cookDayDateMapping.get(assignment.preparedOnDate);
        if (newCookDayDate) {
          updated.preparedOnDate = newCookDayDate;
        }
      }

      return updated;
    });

    // Validate all prep days are still after their cook days - if not, silently reject the move
    for (const assignment of updatedDinners) {
      if (assignment.source === 'assignment_reference' && assignment.preparedOnDate) {
        if (assignment.plannedDate < assignment.preparedOnDate) {
          // Invalid move - prep day would be before cook day, reject silently
          return;
        }
      }
    }

    // Leftover-mode lunches anchor to the MOST RECENT cooked dinner before
    // them (mirrors backend buildLeftoverLunchAssignments: with batch dinners
    // in the mix, "last night" may be a prep day serving days-old food), so
    // after dinners move, each one re-derives its reference. Rotation
    // breakfasts/lunches are untouched.
    const previousDateString = (date: string): string => {
      const [y, m, d] = date.split('-').map(Number);
      const prev = new Date(y, m - 1, d - 1);
      return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-${String(prev.getDate()).padStart(2, '0')}`;
    };
    const cookedDinners = updatedDinners
      .filter(d => d.source !== 'assignment_reference' && !!d.scaledRecipe)
      .sort((x, y) => y.plannedDate.localeCompare(x.plannedDate));
    const updatedOthers = sortedAssignments
      .filter(a => getAssignmentMealType(a) !== 'dinner')
      .map(a => {
        if (getAssignmentMealType(a) === 'lunch' && a.source === 'assignment_reference') {
          const anchor = cookedDinners.find(d => d.plannedDate < a.plannedDate);
          if (anchor?.scaledRecipe) {
            const cookedLastNight = anchor.plannedDate === previousDateString(a.plannedDate);
            return {
              ...a,
              preparedOnDate: anchor.plannedDate,
              reasoning: cookedLastNight
                ? `Leftovers from ${anchor.scaledRecipe.name}. Cook a little extra the night before and lunch is handled.`
                : `Leftovers from ${anchor.scaledRecipe.name}, cooked on ${anchor.plannedDay}. The batch covers this lunch too.`,
            };
          }
        }
        return a;
      });

    const updatedAssignments = [...updatedDinners, ...updatedOthers];
    await persistReorder(updatedAssignments);
  }, [sortedAssignments, dinnerAssignments, mealPlan, persistReorder]);

  // Slot-tab drag end: same shape as the dinner handler, scoped to the active
  // breakfast/lunch rotation. Batch blocks hold together the way meal-prep
  // dinners do: references follow their anchor's new date, and a drop that
  // would eat a batch before it's cooked snaps back silently.
  const handleSlotDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveAssignmentId(null);

    if (!over || active.id === over.id || !mealPlan) return;
    const slot = effectiveMealTab;
    if (slot !== 'breakfast' && slot !== 'lunch') return;

    const oldIndex = slotTabAssignments.findIndex(a => a.id === active.id);
    const newIndex = slotTabAssignments.findIndex(a => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(slotTabAssignments, oldIndex, newIndex);

    // Batch cook (anchor) assignments: old date -> new date, so references
    // can follow their block's cook to wherever it landed
    const anchorDateMapping = new Map<string, string>();
    reordered.forEach((assignment, index) => {
      if (assignment.source !== 'assignment_reference') {
        anchorDateMapping.set(assignment.plannedDate, slotTabAssignments[index].plannedDate);
      }
    });

    const anchorDayByDate = new Map<string, string>();
    const updatedSlot = reordered.map((assignment, index) => {
      const updated = {
        ...assignment,
        plannedDate: slotTabAssignments[index].plannedDate,
        plannedDay: slotTabAssignments[index].plannedDay,
      };
      if (assignment.source === 'assignment_reference' && assignment.preparedOnDate) {
        const newAnchorDate = anchorDateMapping.get(assignment.preparedOnDate);
        if (newAnchorDate) {
          updated.preparedOnDate = newAnchorDate;
        }
      } else {
        // The name references' copy will carry: the anchor's real labor day
        // (a valid cookedOnDate survives the move), not just its eating day
        anchorDayByDate.set(
          updated.plannedDate,
          updated.cookedOnDate && updated.cookedOnDate < updated.plannedDate
            ? (() => {
                const [y, m, d] = updated.cookedOnDate.split('-').map(Number);
                return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' });
              })()
            : updated.plannedDay
        );
      }
      return updated;
    });

    // Causality invariant, same as prep-day dinners: nothing is served before
    // its batch is cooked. Deliberately NOT enforcing the freshness cap here:
    // drag is the escape hatch from our placement opinions.
    for (const assignment of updatedSlot) {
      if (assignment.source === 'assignment_reference' && assignment.preparedOnDate) {
        if (assignment.plannedDate < assignment.preparedOnDate) {
          // Invalid move - reference would precede its cook, reject silently
          return;
        }
      }
    }

    // A moved block's cook-day name changes, so its references' copy follows
    const finalSlot = updatedSlot.map(a =>
      a.source === 'assignment_reference' &&
      a.preparedOnDate &&
      anchorDayByDate.has(a.preparedOnDate)
        ? {
            ...a,
            reasoning: `Made ahead on ${anchorDayByDate.get(a.preparedOnDate)}, ready to grab.`,
          }
        : a
    );

    const updatedAssignments = [
      ...sortedAssignments.filter(a => getAssignmentMealType(a) !== slot),
      ...finalSlot,
    ];
    await persistReorder(updatedAssignments);
  }, [sortedAssignments, slotTabAssignments, effectiveMealTab, mealPlan, persistReorder]);

  // Cook plan: move a cook's labor day without touching any eating day.
  // null = "cook when first served" (clears the stored date). The picker
  // deliberately offers days outside the freshness window: like drag, an
  // explicit move is the escape hatch from our placement opinions. Same-slot
  // references' "Made ahead on X" copy follows the new day (mirrors the
  // backend's stamping rewrite); dinner refs and leftover lunches keep
  // their own copy, which names the serving day, still true.
  const handleChangeCookDay = useCallback(async (
    anchor: MealPlanAssignment,
    newCookDate: string | null
  ) => {
    if (!mealPlan) return;
    const anchorSlot = getAssignmentMealType(anchor);
    const cookDayName = newCookDate
      ? (() => {
          const [y, m, d] = newCookDate.split('-').map(Number);
          return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' });
        })()
      : anchor.plannedDay;
    const updatedAssignments = mealPlan.assignments.map(a => {
      if (a.id === anchor.id) {
        return { ...a, cookedOnDate: newCookDate };
      }
      if (
        a.source === 'assignment_reference' &&
        a.preparedOnDate === anchor.plannedDate &&
        getAssignmentMealType(a) === anchorSlot &&
        a.reasoning?.startsWith('Made ahead on ')
      ) {
        return { ...a, reasoning: `Made ahead on ${cookDayName}, ready to grab.` };
      }
      return a;
    });
    await persistReorder(updatedAssignments, 'Cook day updated');
  }, [mealPlan, persistReorder]);

  // Check if any substitutions are currently active
  const hasActiveSubstitutions = mealPlan?.assignments.some(
    assignment => assignment.substitutionStatus?.status === 'pending' || assignment.substitutionStatus?.status === 'generating'
  ) || substitutingDays.size > 0;

  if (((loading || initialLoading) && !mealPlan) || isDeleting) {
    return (
      <MobileOptimized>
        <Stack gap="lg" pt="xs">
          {/* Header skeleton: back button + title/badge/date + kebab menu */}
          <Group>
            <Skeleton height={34} width={34} radius="md" />
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              {/* Title is ~1.25rem on mobile (.page-header) but full h2 scale
                  on sm+; the date text is ~100px at all sizes */}
              <Group gap="sm">
                <Skeleton h={{ base: 28, sm: 34 }} w={{ base: 110, sm: 160 }} />
                <Skeleton height={20} width={60} radius="xl" />
              </Group>
              <Group gap="sm">
                <Skeleton height={16} width={16} radius="sm" />
                <Skeleton height={16} width="min(100px, 60%)" />
              </Group>
            </Stack>
            <Skeleton height={34} width={34} radius="md" ml="auto" />
          </Group>

          {/* Summary skeleton */}
          <Paper p="md" pb={17} mt={8} bg="gray.0" radius="lg">
            <Stack gap={10}>
              <Skeleton height={14} width="100%" />
              <Skeleton height={14} width="90%" />
              <Skeleton height={14} width="60%" />
              {/* Cost/savings message skeleton */}
              <Skeleton height={12} width="min(280px, 85%)" mt={4} />
            </Stack>
          </Paper>

          {/* Mobile activate button / shopping list skeleton */}
          <Skeleton height={40} radius="md" hiddenFrom="sm" />

          {/* A ?meal= param means the plan is tabbed, so the pill row's
              space is already spoken for */}
          {mealParam && (
            <Group gap="xs">
              {[76, 96, 84, 100].map((w, i) => (
                <Skeleton key={i} height={32} width={w} radius="xl" />
              ))}
            </Group>
          )}

          {/* The cook plan's day-spine of compact rows looks nothing like
              the recipe-card grid, so its skeleton gets its own shape and
              the swap on load doesn't rearrange the whole page */}
          {mealParam === 'cooks' ? (
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              {Array.from({ length: 6 }).map((_, i) => (
                <Paper key={i} radius="lg" p="md" withBorder>
                  <Stack gap="sm">
                    {/* Weekday heading + date */}
                    <Group gap={6} align="baseline">
                      <Skeleton height={14} width={72} />
                      <Skeleton height={10} width={28} />
                    </Group>
                    {/* Cook row: thumbnail + chip, name, coverage, strip */}
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <Skeleton height={52} width={52} radius="md" style={{ flexShrink: 0 }} />
                      <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                        <Skeleton height={16} width={110} radius="xl" />
                        <Skeleton height={14} width="70%" />
                        <Skeleton height={11} width="50%" />
                        <Group gap={3}>
                          {Array.from({ length: 7 }).map((_, day) => (
                            <Skeleton key={day} height={18} width={18} radius={4} />
                          ))}
                        </Group>
                      </Stack>
                    </Group>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          ) : (
          <Grid align="stretch">
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid.Col key={i} span={{ base: 12, md: 6 }}>
                <Card withBorder h="100%" radius="lg">
                  <Stack gap="md">
                    {/* Recipe image with overlaid day badge + heart */}
                    <Box style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
                      <Skeleton height={200} radius={0} />
                      <Skeleton height={24} width={80} radius="xl" style={{ position: 'absolute', top: 8, left: 8 }} />
                      <Skeleton height={24} width={24} radius="xl" style={{ position: 'absolute', top: 8, right: 8 }} />
                    </Box>
                    {/* Recipe name */}
                    <Skeleton height={18} width="70%" />
                    {/* Description */}
                    <Skeleton height={14} width="95%" />
                    <Skeleton height={14} width="70%" />
                    {/* Metadata: time, servings, difficulty, cost */}
                    <Group gap="xs">
                      <Skeleton height={18} width={60} radius="xl" />
                      <Skeleton height={18} width={80} radius="xl" />
                      <Skeleton height={18} width={50} radius="xl" />
                      <Skeleton height={18} width={95} radius="xl" />
                    </Group>
                    {/* Cuisine + dietary/tags badges */}
                    <Group gap="xs">
                      <Skeleton height={18} width={70} radius="xl" />
                      <Skeleton height={18} width={90} radius="xl" />
                      <Skeleton height={18} width={60} radius="xl" />
                      <Skeleton height={18} width={80} radius="xl" />
                    </Group>
                    {/* "N more ideas" chip + carousel dots */}
                    <Stack gap={10} align="center">
                      <Skeleton height={12} width={90} radius="xl" />
                      <Group gap={6} justify="center">
                        <Skeleton height={8} width={16} radius="xl" />
                        <Skeleton height={8} width={8} circle />
                        <Skeleton height={8} width={8} circle />
                      </Group>
                    </Stack>
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
          )}
        </Stack>
      </MobileOptimized>
    );
  }

  if (error && !mealPlan) {
    return (
      <MobileOptimized>
        <Alert icon={<IconAlertCircle size={16} color="red" />} color="red" radius="lg">
          <Text size="sm" c="red.7">{error}</Text>
        </Alert>
      </MobileOptimized>
    );
  }

  if (!mealPlan) {
    return (
      <MobileOptimized>
        <Text>Meal plan not found</Text>
      </MobileOptimized>
    );
  }

  // Redirect to dashboard for generation status - notifications will show progress there
  if (isGenerating) {
    return null; // Don't render anything while redirecting
  }

  if (mealPlan.generationStatus.status === 'failed') {
    return (
      <MobileOptimized>
        <Alert icon={<IconAlertCircle size={16} color="red" />} color="red" radius="lg">
          <Stack gap="sm">
            <Text size="sm" c="red.7" fw={500}>Failed to create meal plan</Text>
            <Text size="sm" c="red.7">{mealPlan.generationStatus.errorMessage || 'An unexpected error occurred'}</Text>
            {onBack && (
              <Button variant="light" onClick={onBack} leftSection={<IconChevronLeft size={16} />}>
                Back to Dashboard
              </Button>
            )}
          </Stack>
        </Alert>
      </MobileOptimized>
    );
  }

  const canModifyPlan = isOwner && !planIsPast;
  // Mirrors the Remove-day menu item's disabled conditions so a day can't be
  // restored while activation or grocery list generation is in progress
  const dayActionsBusy =
    loadingActivation ||
    mealPlan?.groceryListStatus?.status === 'pending' ||
    mealPlan?.groceryListStatus?.status === 'generating';

  const handleAssignmentClick = (assignment: MealPlanAssignment, overrideRecipe?: Recipe) => {
    const recipe = overrideRecipe || getAssignmentRecipe(assignment, mealPlan);
    if (!recipe) return;

    // Flush any pending carousel swap before navigating away (fire-and-forget:
    // the fetch completes in the background even after unmount)
    const flushCallback = pendingSwapCallbacksRef.current.get(assignment.id);
    if (flushCallback) {
      flushCallback();
    }

    navigate(buildRecipePath(recipe, userId || owner?.uid || ''), {
      state: {
        // Only pass recipe state if it differs from canonical recipe
        recipe: isRecipeScaled(recipe) ? recipe : undefined,
        mealPlanId: effectivePlanId,
        assignmentId: assignment.id
      }
    });
  };

  return (
    <MobileOptimized>
      <Stack gap="lg">
        {/* Header */}
        <MealPlanHeader
          mealPlan={mealPlan}
          userId={userId}
          onBack={onBack}
          onDelete={isOwner ? onDelete : undefined}
          onActivatePlan={handleActivatePlan}
          onRegenerateGroceryList={isOwner ? handleRegenerateGroceryList : undefined}
          onOpenPreferences={isOwner ? () => setPreferencesModalOpen(true) : undefined}
          onMealPlanFeedback={isOwner ? handleMealPlanFeedback : undefined}
          onToggleShare={isOwner ? handleToggleShare : undefined}
          onShareLink={isOwner ? handleShareLink : undefined}
          onSyncCalendar={isOwner ? handleSyncCalendar : undefined}
          onRemoveCalendar={isOwner ? handleRemoveCalendar : undefined}
          loadingActivation={loadingActivation}
          loadingRegeneration={loadingActivation}
          hasNoMeals={sortedAssignments.length === 0}
          loadingCalendarSync={loadingCalendarSync}
          hasActiveSubstitutions={hasActiveSubstitutions}
          getStatusColor={getStatusColor}
          isOwner={isOwner}
          profilePublic={user?.profilePublic}
          calendarConnected={user?.familyProfile?.googleCalendarConnected ?? false}
          calendarSyncedAt={mealPlan.calendarSyncedAt}
        />

        {/* Owner Attribution (for non-owners viewing shared meal plans) */}
        {!isOwner && owner && (
          <Paper
            px="md"
            py={14}
            radius="lg"
            withBorder
            style={{ cursor: 'pointer', backgroundColor: 'var(--mantine-color-gray-0)' }}
            onClick={() => {
              const ownerUid = userId || owner.uid;
              if (ownerUid) {
                navigate(`/u/${ownerUid}`);
              }
            }}
          >
            <Group gap="sm">
              <UserAvatar
                photoURL={owner.photoURL}
                displayName={owner.displayName}
                size="md"
                style={{ opacity: 0, animation: 'avatarFadeIn 0.3s ease forwards' }}
              />
              <style>{`@keyframes avatarFadeIn { to { opacity: 1 } }`}</style>
              <Box style={{ flex: 1 }}>
                <Text size="sm" c="dimmed">Meal plan by</Text>
                <Text fw={500}>{ownerDisplayName(owner.displayName)}</Text>
              </Box>
              <Text size="sm" c="blue">View Profile</Text>
            </Group>
          </Paper>
        )}

        {/* Meal Plan Summary */}
        <MealPlanSummary
          mealPlan={mealPlan}
          summaryExpanded={summaryExpanded}
          setSummaryExpanded={setSummaryExpanded}
          openTooltip={openTooltip}
          setOpenTooltip={setOpenTooltip}
        />

        {/* Mobile Activate Button */}
        {canModifyPlan && mealPlan.status === 'draft' && (
          <Button
            leftSection={<IconCheck size={16} />}
            onClick={handleActivatePlan}
            loading={loadingActivation}
            disabled={hasActiveSubstitutions || sortedAssignments.length === 0}
            hiddenFrom="sm"
            fullWidth
            color="indigo"
            data-tour="activate-button-mobile"
          >
            {loadingActivation
              ? 'Activating Plan...'
                : 'Activate Plan'
            }
          </Button>
        )}

        {/* First-swap prompt: floating one-shot nudge to try an alternative.
            Fixed positioning lives on the outer box so the Transition's
            animated transform doesn't fight the translateX centering. */}
        <Box
          style={{
            position: 'fixed',
            bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 150,
            width: 'min(92vw, 440px)',
            pointerEvents: 'none',
          }}
        >
          <Transition
            mounted={firstSwapBannerVisible}
            duration={prefersReducedMotion ? 0 : 300}
            exitDuration={prefersReducedMotion ? 0 : 250}
            timingFunction="ease"
            transition={{
              in: { opacity: 1, transform: 'translateY(0)' },
              out: { opacity: 0, transform: 'translateY(14px)' },
              transitionProperty: 'opacity, transform',
            }}
          >
            {(transitionStyles) => (
          <Paper
            shadow="xl"
            radius="lg"
            p="md"
            style={{
              ...transitionStyles,
              pointerEvents: 'auto',
              border: '1px solid var(--mantine-color-orange-2)',
              background: 'linear-gradient(135deg, #fff9f4 0%, #ffffff 55%)',
            }}
          >
            <Group gap="md" wrap="nowrap" align="center">
              <ThemeIcon
                size={40}
                radius="xl"
                variant="gradient"
                gradient={{ from: 'orange', to: 'red' }}
                style={{ flexShrink: 0 }}
              >
                <IconBulb size={22} />
              </ThemeIcon>
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={600} c="dark">
                  Your plan is a starting point
                </Text>
                <Text size="xs" c="dimmed">
                  {isCoarsePointer ? (
                    <>
                      Not sold on a meal? Swipe it or tap <Text span size="xs" fw={600} c="orange.7">more ideas</Text> beneath
                      it to swap to an alternative.
                    </>
                  ) : (
                    <>
                      Not sold on a meal? Use the arrows or click <Text span size="xs" fw={600} c="orange.7">more ideas</Text> beneath
                      it to swap to an alternative.
                    </>
                  )}
                </Text>
              </Stack>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => completeFirstSwapPrompt('dismissed')}
                aria-label="Dismiss"
                style={{ alignSelf: 'flex-start', flexShrink: 0 }}
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
          </Paper>
            )}
          </Transition>
        </Box>

        {/* Mobile Regenerate Button */}
        {canModifyPlan && mealPlan.status === 'active' && mealPlan.lastModifiedAt && mealPlan.groceryListGeneratedAt && new Date(mealPlan.lastModifiedAt) > new Date(mealPlan.groceryListGeneratedAt) && (
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={handleRegenerateGroceryList}
            loading={loadingActivation || mealPlan.groceryListStatus?.status === 'pending' || mealPlan.groceryListStatus?.status === 'generating'}
            disabled={hasActiveSubstitutions || loadingActivation || mealPlan.groceryListStatus?.status === 'pending' || mealPlan.groceryListStatus?.status === 'generating'}
            hiddenFrom="sm"
            fullWidth
            variant="light"
            color="blue"
          >
            {loadingActivation || mealPlan.groceryListStatus?.status === 'pending' || mealPlan.groceryListStatus?.status === 'generating'
              ? 'Refreshing...'
                : 'Refresh shopping list'
            }
          </Button>
        )}


        {/* Shopping List */}
        <ShoppingList
          mealPlan={mealPlan}
          groceryList={groceryList}
          addingToCart={addingToCart}
          cartProgress={cartProgress}
          cartResults={cartResults}
          setCartModalOpen={setCartModalOpen}
          initialCartCheckComplete={initialCartCheckComplete}
          isOwner={canModifyPlan}
          isMobile={isMobile}
          isKrogerConnected={isKrogerConnected}
          handleAddToKrogerCart={handleAddToKrogerCart}
          handleAddToInstacart={handleAddToInstacart}
          addingToInstacart={addingToInstacart}
          setAdditionalItemsDrawerOpen={setAdditionalItemsDrawerOpen}
          onOpenList={() => setShoppingListDrawerOpen(true)}
          onRetryGeneration={handleRegenerateGroceryList}
        />

        {/* Meal tabs: plans with breakfast/lunch switch the whole grid between
            slots. Dinner is the only draggable view; breakfast/lunch reuse the
            same day cards (leftover lunches render via the meal-prep reference
            treatment) without drag handles. */}
        {mealTabOptions.length > 1 && (
          <Box ref={mealTabsWrapRef}>
          <Tabs
            value={effectiveMealTab}
            onChange={(value) => value && handleMealTabChange(value)}
            variant="pills"
            radius="xl"
            classNames={{ list: viewStyles.mealTabsList, tab: viewStyles.mealTab }}
          >
            <Tabs.List>
              {mealTabOptions.map((option) => (
                <Tabs.Tab key={option.value} value={option.value} px={isMobile ? 'xs' : undefined}>
                  {option.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
          </Box>
        )}

        {/* Recipe Cards */}
        <Box ref={mealTabPanelRef}>
        {effectiveMealTab === 'cooks' ? (
          <CookScheduleView
            mealPlan={mealPlan}
            onOpen={(a, recipe) => handleAssignmentClick(a, recipe)}
            onChangeCookDay={isOwner && !planIsPast ? handleChangeCookDay : undefined}
          />
        ) : effectiveMealTab !== 'dinner' ? (
          <DndContext
            sensors={slotDragEnabled && !hasActiveSubstitutions ? sensors : []}
            collisionDetection={closestCenter}
            onDragStart={handleAssignmentDragStart}
            onDragEnd={handleSlotDragEnd}
          >
          <SortableContext
            items={slotTabAssignments.map(a => a.id)}
            strategy={isTwoColumnPlanGrid ? rectSortingStrategy : verticalListSortingStrategy}
          >
          <Grid align="stretch">
            {slotTabCells.map(({ date, day, assignment }) => {
              // A removed meal's placeholder takes the empty cell's spot so
              // the slot stays restorable after the undo toast is gone
              const deletedDay =
                assignment === null
                  ? deletedDayCells.find(
                      d => d.plannedDate === date && (d.mealType ?? 'dinner') === effectiveMealTab
                    )
                  : undefined;
              return (
              deletedDay ? (
                <Grid.Col key={`deleted-${date}`} span={{ base: 12, md: 6 }}>
                  <DeletedDayCard
                    day={deletedDay}
                    restoring={restoringDays.has(`${date}#${effectiveMealTab}`)}
                    disabled={dayActionsBusy || !confirmedDeletedKeys.has(deletedDayKey(deletedDay))}
                    onRestore={() => handleRestoreDay(date, effectiveMealTab)}
                    onPickDifferent={() => handleRestoreAndPickDifferent(deletedDay)}
                    onAddCustomMeal={() => setCustomMealTarget({
                      mode: 'add',
                      plannedDate: date,
                      plannedDay: day,
                      mealType: effectiveMealTab,
                    })}
                  />
                </Grid.Col>
              ) : assignment === null ? (
                <Grid.Col key={`empty-${date}`} span={{ base: 12, md: 6 }}>
                  <Paper
                    radius="lg"
                    withBorder
                    p="xl"
                    h="100%"
                    style={{
                      borderStyle: 'dashed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 180,
                    }}
                  >
                    <Stack gap={4} align="center">
                      <Text fw={600} c="dimmed">{day}</Text>
                      <Text size="sm" c="dimmed">
                        No {effectiveMealTab} planned
                      </Text>
                      {addingSlots.has(`${date}#${effectiveMealTab}`) ? (
                        <Group gap="xs" mt={6}>
                          <Loader size="xs" />
                          <Text size="sm" c="dimmed">
                            Adding your {effectiveMealTab}...
                          </Text>
                        </Group>
                      ) : canModifyPlan && !planIsPast ? (
                        <Group gap="xs" mt={6}>
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconPlus size={14} />}
                            disabled={slotAddDisabled}
                            onClick={() => handleAddSlotMeal(date, effectiveMealTab)}
                          >
                            Pick for me
                          </Button>
                          <Button
                            size="xs"
                            variant="subtle"
                            disabled={slotAddDisabled}
                            onClick={() => handleAddFromCookbook(date, effectiveMealTab)}
                          >
                            From cookbook
                          </Button>
                        </Group>
                      ) : null}
                    </Stack>
                  </Paper>
                </Grid.Col>
              ) : assignment.source === 'custom_meal' ? (
                <Grid.Col key={assignment.id} span={{ base: 12, md: 6 }}>
                  <SortableCustomMealCard
                    id={assignment.id}
                    dragDisabled={hasActiveSubstitutions || !slotDragEnabled}
                  >
                    {(dragHandle) => (
                      <CustomMealCard
                        assignment={assignment}
                        canModify={canModifyPlan}
                        actionsDisabled={dayActionsBusy}
                        onSwap={() => handleSubstituteWithInstructions(assignment.plannedDay, effectiveMealTab)}
                        swapping={
                          // Composite key: slot swaps register as day#mealType
                          // (bare day is dinner's), and the bare-day check
                          // left this card spinnerless until the server's
                          // pending status round-tripped
                          substitutingDays.has(assignmentSubstitutionKey(assignment)) ||
                          assignment.substitutionStatus?.status === 'pending' ||
                          assignment.substitutionStatus?.status === 'generating'
                        }
                        substitutionProgress={substitutionProgressByKey?.[assignmentSubstitutionKey(assignment)]}
                        household={mealPlan?.profileSnapshot}
                        onEdit={() => setCustomMealTarget({ mode: 'edit', assignment })}
                        onDelete={() => handleDeleteAssignment(assignment)}
                        dragHandle={slotDragEnabled ? dragHandle : undefined}
                      />
                    )}
                  </SortableCustomMealCard>
                </Grid.Col>
              ) : (() => {
                const card = (
                  <RecipeCard
                    assignment={assignment}
                    isOwner={isOwner}
                    mealPlan={mealPlan}
                    cookDayPreviews={cookDayPreviews}
                    onCookDayPreview={handleCookDayPreview}
                    loadingActivation={loadingActivation}
                    menuOpen={menuOpen}
                    openTooltip={openTooltip}
                    servingsInputMode={servingsInputMode}
                    tempServings={tempServings}
                    savingDays={savingDays}
                    substitutingDays={substitutingDays}
                    substitutionProgressByKey={substitutionProgressByKey}
                    swappingDays={swappingDays}
                    ratingDays={ratingDays}
                    handleAssignmentClick={handleAssignmentClick}
                    handleToggleSaveRecipe={handleToggleSaveRecipe}
                    handleTooltipClick={handleTooltipClick}
                    handleSubstituteWithInstructions={(day) => handleSubstituteWithInstructions(day, effectiveMealTab)}
                    handleServingsInputMode={handleServingsInputMode}
                    handleSubmitServings={(day) => handleSubmitServings(day, effectiveMealTab)}
                    handleAssignmentFeedback={handleAssignmentFeedback}
                    handleRateUp={handleRateUp}
                    handleRateDown={handleRateDown}
                    handleUndoRating={handleUndoRating}
                    handleDeleteAssignment={handleDeleteAssignment}
                    handleSwapAlternative={handleSwapAlternative}
                    setMenuOpen={setMenuOpen}
                    setServingsInputMode={setServingsInputMode}
                    setTempServings={setTempServings}
                    setSelectedRecipeForNutrition={setSelectedRecipeForNutrition}
                    setNutritionDrawerOpened={setNutritionDrawerOpened}
                    isFirstCard={false}
                    userDietaryRestrictions={userLoading ? null : !user ? [] : userProfile ? userProfile.dietaryRestrictions : null}
                    userHouseholdSegment={householdSegment(userProfile ?? undefined)}
                    radius="lg"
                    pendingSwapCallbacksRef={pendingSwapCallbacksRef}
                  />
                );
                return (
                  <Grid.Col key={assignment.id} span={{ base: 12, md: 6 }}>
                    {slotDragEnabled ? (
                      // The generic sortable shell: grip handle, transform,
                      // dim-while-dragging (same treatment as dinner cards)
                      <SortableCustomMealCard
                        id={assignment.id}
                        dragDisabled={hasActiveSubstitutions}
                      >
                        {(dragHandle) => (
                          <>
                            {dragHandle}
                            {card}
                          </>
                        )}
                      </SortableCustomMealCard>
                    ) : (
                      card
                    )}
                  </Grid.Col>
                );
              })()
            );
            })}
          </Grid>
          </SortableContext>

          <DragOverlay>
            {activeAssignmentId && (() => {
              const dragged = slotTabAssignments.find(a => a.id === activeAssignmentId);
              if (!dragged) return null;
              return (
                <Box style={{ cursor: 'grabbing', transform: 'rotate(-2deg)', position: 'relative', height: '100%', borderRadius: 'var(--mantine-radius-lg)', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.18)' }}>
                  <MealCardDragHandle grabbing />
                  {/* Visual clone only: the real handlers are inert mid-drag */}
                  <RecipeCard
                    assignment={dragged}
                    isOwner={isOwner}
                    mealPlan={mealPlan}
                    loadingActivation={loadingActivation}
                    menuOpen={{}}
                    openTooltip={null}
                    servingsInputMode={null}
                    tempServings={tempServings}
                    savingDays={savingDays}
                    substitutingDays={substitutingDays}
                    swappingDays={swappingDays}
                    ratingDays={ratingDays}
                    handleAssignmentClick={handleAssignmentClick}
                    handleToggleSaveRecipe={handleToggleSaveRecipe}
                    handleTooltipClick={handleTooltipClick}
                    handleSubstituteWithInstructions={(day) => handleSubstituteWithInstructions(day, effectiveMealTab)}
                    handleServingsInputMode={handleServingsInputMode}
                    handleSubmitServings={(day) => handleSubmitServings(day, effectiveMealTab)}
                    handleAssignmentFeedback={handleAssignmentFeedback}
                    handleRateUp={handleRateUp}
                    handleRateDown={handleRateDown}
                    handleUndoRating={handleUndoRating}
                    handleDeleteAssignment={handleDeleteAssignment}
                    handleSwapAlternative={handleSwapAlternative}
                    setMenuOpen={setMenuOpen}
                    setServingsInputMode={setServingsInputMode}
                    setTempServings={setTempServings}
                    setSelectedRecipeForNutrition={setSelectedRecipeForNutrition}
                    setNutritionDrawerOpened={setNutritionDrawerOpened}
                    isFirstCard={false}
                    userDietaryRestrictions={userLoading ? null : !user ? [] : userProfile ? userProfile.dietaryRestrictions : null}
                    userHouseholdSegment={householdSegment(userProfile ?? undefined)}
                    radius="lg"
                    pendingSwapCallbacksRef={pendingSwapCallbacksRef}
                  />
                </Box>
              );
            })()}
          </DragOverlay>
          </DndContext>
        ) : canModifyPlan ? (
          <DndContext
            sensors={hasActiveSubstitutions ? [] : sensors}
            collisionDetection={closestCenter}
            onDragStart={handleAssignmentDragStart}
            onDragEnd={handleAssignmentDragEnd}
          >
            <SortableContext
              items={dinnerAssignments.map(a => a.id)}
              // The strategy and the row sizing travel together. Two-column:
              // equalized rows put every card in one height class, which is
              // what keeps rectSortingStrategy's transforms scale-free. Single
              // column: rows take natural heights (short penciled cards don't
              // stretch), and the vertical-list strategy is the one that
              // handles variable heights without cards colliding mid-drag.
              strategy={isTwoColumnPlanGrid ? rectSortingStrategy : verticalListSortingStrategy}
            >
              <SimpleGrid
            cols={{ base: 1, md: 2 }}
            spacing="md"
            style={{ gridAutoRows: isTwoColumnPlanGrid ? '1fr' : undefined }}
          >
                {planGridCells.map((cell) => cell.kind === 'deleted' ? (
                  <Box h="100%" key={`deleted-${cell.day.plannedDate}`}>
                    <DeletedDayCard
                      day={cell.day}
                      restoring={restoringDays.has(cell.day.plannedDate)}
                      disabled={dayActionsBusy || !confirmedDeletedKeys.has(cell.day.plannedDate)}
                      onRestore={() => handleRestoreDay(cell.day.plannedDate)}
                      onPickDifferent={() => handleRestoreAndPickDifferent(cell.day)}
                      onAddCustomMeal={() => setCustomMealTarget({
                        mode: 'add',
                        plannedDate: cell.day.plannedDate,
                        plannedDay: cell.day.plannedDay,
                        mealType: 'dinner',
                      })}
                    />
                  </Box>
                ) : cell.assignment.source === 'custom_meal' ? (
                  <Box h="100%" key={cell.assignment.id}>
                    <SortableCustomMealCard
                      id={cell.assignment.id}
                      dragDisabled={hasActiveSubstitutions}
                    >
                      {(dragHandle) => (
                        <CustomMealCard
                          assignment={cell.assignment}
                          canModify={canModifyPlan}
                          actionsDisabled={dayActionsBusy}
                          onSwap={() => handleSubstituteWithInstructions(cell.assignment.plannedDay)}
                          swapping={
                            substitutingDays.has(cell.assignment.plannedDay) ||
                            cell.assignment.substitutionStatus?.status === 'pending' ||
                            cell.assignment.substitutionStatus?.status === 'generating'
                          }
                          substitutionProgress={substitutionProgressByKey?.[assignmentSubstitutionKey(cell.assignment)]}
                          household={mealPlan?.profileSnapshot}
                          onEdit={() => setCustomMealTarget({ mode: 'edit', assignment: cell.assignment })}
                          onDelete={() => handleDeleteAssignment(cell.assignment)}
                          dragHandle={dragHandle}
                        />
                      )}
                    </SortableCustomMealCard>
                  </Box>
                ) : (
                  <Box h="100%" key={cell.assignment.id}>
                    <SortableMealCard
                      id={cell.assignment.id}
                      assignment={cell.assignment}
                      isOwner={isOwner}
                      mealPlan={mealPlan}
                      loadingActivation={loadingActivation}
                      menuOpen={menuOpen}
                      openTooltip={openTooltip}
                      servingsInputMode={servingsInputMode}
                      tempServings={tempServings}
                      savingDays={savingDays}
                      substitutingDays={substitutingDays}
                      substitutionProgressByKey={substitutionProgressByKey}
                      swappingDays={swappingDays}
                      ratingDays={ratingDays}
                      dragDisabled={hasActiveSubstitutions}
                      handleAssignmentClick={handleAssignmentClick}
                      handleToggleSaveRecipe={handleToggleSaveRecipe}
                      handleTooltipClick={handleTooltipClick}
                      handleSubstituteWithInstructions={handleSubstituteWithInstructions}
                      handleServingsInputMode={handleServingsInputMode}
                      handleSubmitServings={handleSubmitServings}
                      handleAssignmentFeedback={handleAssignmentFeedback}
                      handleRateUp={handleRateUp}
                      handleRateDown={handleRateDown}
                      handleUndoRating={handleUndoRating}
                      handleDeleteAssignment={handleDeleteAssignment}
                      handleSwapAlternative={handleSwapAlternative}
                      setMenuOpen={setMenuOpen}
                      setServingsInputMode={setServingsInputMode}
                      setTempServings={setTempServings}
                      setSelectedRecipeForNutrition={setSelectedRecipeForNutrition}
                      setNutritionDrawerOpened={setNutritionDrawerOpened}
                      isFirstCard={cell.assignment.id === firstRecipeAssignmentId}
                      userDietaryRestrictions={userLoading ? null : !user ? [] : userProfile ? userProfile.dietaryRestrictions : null}
                      userHouseholdSegment={householdSegment(userProfile ?? undefined)}
                      radius="lg"
                      pendingSwapCallbacksRef={pendingSwapCallbacksRef}
                      cookDayPreviews={cookDayPreviews}
                      onCookDayPreview={handleCookDayPreview}
                      onFlipChange={(flippedRecipeId) => {
                        flippedRecipeByAssignmentRef.current[cell.assignment.id] = flippedRecipeId;
                      }}
                    />
                  </Box>
                ))}
              </SimpleGrid>
            </SortableContext>

            <DragOverlay>
              {activeAssignmentId && sortedAssignments.find(a => a.id === activeAssignmentId)?.source === 'custom_meal' ? (
                // height: 100% chains the DragOverlay's measured rect through to
                // the card's h="100%", so the clone matches the grid-stretched
                // height instead of collapsing to the card's natural minHeight
                <Box style={{ cursor: 'grabbing', transform: 'rotate(-2deg)', position: 'relative', height: '100%', borderRadius: 'var(--mantine-radius-lg)', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.18)' }}>
                  <CustomMealCard
                    assignment={sortedAssignments.find(a => a.id === activeAssignmentId)!}
                    canModify={false}
                    actionsDisabled
                    onSwap={() => {}}
                    onEdit={() => {}}
                    onDelete={() => {}}
                    dragHandle={<MealCardDragHandle grabbing />}
                  />
                </Box>
              ) : activeAssignmentId ? (
                <Box style={{ cursor: 'grabbing', transform: 'rotate(-2deg)', position: 'relative', borderRadius: 'var(--mantine-radius-lg)', boxShadow: '0 8px 20px rgba(0, 0, 0, 0.18)' }}>
                  <RecipeCard
                    dragHandle={<MealCardDragHandle grabbing />}
                    initialFlippedRecipeId={flippedRecipeByAssignmentRef.current[activeAssignmentId] ?? null}
                    assignment={sortedAssignments.find(a => a.id === activeAssignmentId)!}
                    isOwner={isOwner}
                    mealPlan={mealPlan}
                    loadingActivation={loadingActivation}
                    menuOpen={menuOpen}
                    openTooltip={openTooltip}
                    servingsInputMode={servingsInputMode}
                    tempServings={tempServings}
                    savingDays={savingDays}
                    substitutingDays={substitutingDays}
                    substitutionProgressByKey={substitutionProgressByKey}
                    swappingDays={swappingDays}
                    ratingDays={ratingDays}
                    handleAssignmentClick={handleAssignmentClick}
                    handleToggleSaveRecipe={handleToggleSaveRecipe}
                    handleTooltipClick={handleTooltipClick}
                    handleSubstituteWithInstructions={handleSubstituteWithInstructions}
                    handleServingsInputMode={handleServingsInputMode}
                    handleSubmitServings={handleSubmitServings}
                    handleAssignmentFeedback={handleAssignmentFeedback}
                      handleRateUp={handleRateUp}
                      handleRateDown={handleRateDown}
                      handleUndoRating={handleUndoRating}
                    handleDeleteAssignment={handleDeleteAssignment}
                    handleSwapAlternative={handleSwapAlternative}
                    setMenuOpen={setMenuOpen}
                    setServingsInputMode={setServingsInputMode}
                    setTempServings={setTempServings}
                    setSelectedRecipeForNutrition={setSelectedRecipeForNutrition}
                    setNutritionDrawerOpened={setNutritionDrawerOpened}
                    userDietaryRestrictions={userLoading ? null : !user ? [] : userProfile ? userProfile.dietaryRestrictions : null}
                    userHouseholdSegment={householdSegment(userProfile ?? undefined)}
                    radius="lg"
                  />
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <SimpleGrid
            cols={{ base: 1, md: 2 }}
            spacing="md"
            style={{ gridAutoRows: isTwoColumnPlanGrid ? '1fr' : undefined }}
          >
            {dinnerAssignments.map((assignment, index) => assignment.source === 'custom_meal' ? (
              <Box h="100%" key={assignment.id}>
                <CustomMealCard
                  assignment={assignment}
                  canModify={false}
                  actionsDisabled
                  onSwap={() => {}}
                  onEdit={() => {}}
                  onDelete={() => {}}
                />
              </Box>
            ) : (
              <Box h="100%" key={assignment.id}>
                <RecipeCard
                  assignment={assignment}
                  isOwner={isOwner}
                  mealPlan={mealPlan}
                  loadingActivation={loadingActivation}
                  menuOpen={menuOpen}
                  openTooltip={openTooltip}
                  servingsInputMode={servingsInputMode}
                  tempServings={tempServings}
                  savingDays={savingDays}
                  substitutingDays={substitutingDays}
                  substitutionProgressByKey={substitutionProgressByKey}
                  swappingDays={swappingDays}
                  ratingDays={ratingDays}
                  handleAssignmentClick={handleAssignmentClick}
                  handleToggleSaveRecipe={handleToggleSaveRecipe}
                  handleTooltipClick={handleTooltipClick}
                  handleSubstituteWithInstructions={handleSubstituteWithInstructions}
                  handleServingsInputMode={handleServingsInputMode}
                  handleSubmitServings={handleSubmitServings}
                  handleAssignmentFeedback={handleAssignmentFeedback}
                      handleRateUp={handleRateUp}
                      handleRateDown={handleRateDown}
                      handleUndoRating={handleUndoRating}
                  handleDeleteAssignment={handleDeleteAssignment}
                  handleSwapAlternative={handleSwapAlternative}
                  setMenuOpen={setMenuOpen}
                  setServingsInputMode={setServingsInputMode}
                  setTempServings={setTempServings}
                  setSelectedRecipeForNutrition={setSelectedRecipeForNutrition}
                  setNutritionDrawerOpened={setNutritionDrawerOpened}
                  isFirstCard={index === 0}
                  userDietaryRestrictions={userLoading ? null : !user ? [] : userProfile ? userProfile.dietaryRestrictions : null}
                  userHouseholdSegment={householdSegment(userProfile ?? undefined)}
                  radius="lg"
                  pendingSwapCallbacksRef={pendingSwapCallbacksRef}
                  cookDayPreviews={cookDayPreviews}
                  onCookDayPreview={handleCookDayPreview}
                />
              </Box>
            ))}
          </SimpleGrid>
        )}
        </Box>

        {/* Kroger Cart Results Drawer */}
        <KrogerCartResultsDrawer
          opened={cartModalOpen}
          onClose={() => setCartModalOpen(false)}
          cartResults={cartResults}
          userProfileDomain={userProfile?.krogerInfo?.location?.domain}
          onCheckOffAddedIngredients={handleCheckOffAddedIngredients}
        />

        {/* Recipe Substitution with Instructions Drawer */}
        <RecipeCustomizationDrawer
          opened={substitutionModalOpen}
          onClose={() => setSubstitutionModalOpen(false)}
          recipe={(() => {
            const assignment = mealPlan?.assignments.find(
              a =>
                a.plannedDay === selectedDayForSubstitution &&
                getAssignmentMealType(a) === selectedMealTypeForSubstitution
            );
            return assignment ? getAssignmentRecipe(assignment, mealPlan) ?? undefined : undefined;
          })()}
          penciledName={(() => {
            const assignment = mealPlan?.assignments.find(
              a =>
                a.plannedDay === selectedDayForSubstitution &&
                getAssignmentMealType(a) === selectedMealTypeForSubstitution
            );
            return assignment?.source === 'custom_meal' ? assignment.customMeal?.name : undefined;
          })()}
          selectedDay={selectedDayForSubstitution}
          kids={user?.familyProfile?.kids ?? 0}
          avoidTerms={substitutionAvoidTerms}
          reasons={substitutionReasons}
          onReasonsChange={setSubstitutionReasons}
          alwaysAvoid={substitutionAlwaysAvoid}
          onAlwaysAvoidChange={setSubstitutionAlwaysAvoid}
          instructions={substitutionContext}
          onInstructionsChange={setSubstitutionContext}
          modifyExisting={modifyExisting}
          onModifyExistingChange={setModifyExisting}
          onSubmit={handleSubmitInstructions}
          onChooseFromCookbook={() => {
            setSubstitutionModalOpen(false);
            handleSubstituteFromCookbook(selectedDayForSubstitution, selectedMealTypeForSubstitution);
          }}
          onPencilIn={(() => {
            // Matched by day AND slot: with breakfast/lunch planned, a day
            // holds several assignments and day alone grabs an arbitrary one
            const assignment = mealPlan?.assignments.find(
              a =>
                a.plannedDay === selectedDayForSubstitution &&
                getAssignmentMealType(a) === selectedMealTypeForSubstitution
            );
            if (!assignment) {
              return undefined;
            }
            // A cook day other days eat from can't become a custom meal (no
            // batch for the references to draw on); the backend refuses it,
            // so the drawer doesn't offer it. References CAN be penciled
            // over: the day detaches from its block ("we're eating out
            // Tuesday instead of leftovers").
            const feedsOtherDays =
              assignment.isCookDay &&
              mealPlan?.assignments.some(
                a =>
                  a.source === 'assignment_reference' &&
                  getReferenceAnchor(a, mealPlan)?.id === assignment.id
              );
            if (feedsOtherDays) return undefined;
            return () => {
              setSubstitutionModalOpen(false);
              handlePencilInReplace(assignment);
            };
          })()}
          quotaExhausted={
            !!mealPlan && mealPlan.aiSubstitutionsUsed >= mealPlan.maxAiSubstitutions
          }
        />

        {/* Recipe Selection Drawer */}
        <RecipeSelectionDrawer
          opened={recipeSelectionModalOpen}
          onClose={() => {
            setRecipeSelectionModalOpen(false);
            setAddTarget(null);
            // Drop the substitution context on close, otherwise a later add
            // flow inherits this swap's "replacing ..." header
            setSelectedDayForRecipeSelection('');
            setSelectedMealTypeForRecipeSelection('dinner');
          }}
          onBack={() => {
            setRecipeSelectionModalOpen(false);
            setAddTarget(null);
            setSubstitutionModalOpen(true);
          }}
          onSelectRecipe={handleSelectRecipe}
          onRemixSubmission={handleRemixSubmission}
          selectedDay={selectedDayForRecipeSelection}
          // Data-driven like the slot tabs: a dinner-only plan gets the
          // pre-slot drawer (no meal filter, every recipe visible), so the
          // slots backfill never hides a non-flagged user's sides or desserts
          mealTypeContext={
            hasBreakfastSlots || hasLunchSlots
              ? addTarget?.mealType ?? selectedMealTypeForRecipeSelection
              : undefined
          }
          replacingRecipeName={(() => {
            if (addTarget) return undefined; // adds replace nothing
            const assignment = mealPlan?.assignments.find(
              a =>
                a.plannedDay === selectedDayForRecipeSelection &&
                getAssignmentMealType(a) === selectedMealTypeForRecipeSelection
            );
            return assignment ? getAssignmentRecipe(assignment, mealPlan)?.name : undefined;
          })()}
          replacingRecipeId={(() => {
            if (addTarget) return undefined;
            const assignment = mealPlan?.assignments.find(
              a =>
                a.plannedDay === selectedDayForRecipeSelection &&
                getAssignmentMealType(a) === selectedMealTypeForRecipeSelection
            );
            return assignment ? getAssignmentRecipe(assignment, mealPlan)?.id : undefined;
          })()}
          userRecipes={userRecipes}
          loading={loadingRecipes}
          mealPlan={mealPlan}
          userDietaryRestrictions={userProfile?.dietaryRestrictions}
          userHouseholdSegment={householdSegment(userProfile ?? undefined)}
        />

        {/* Add / edit a custom (recipe-less) meal */}
        <CustomMealDrawer
          opened={customMealTarget !== null}
          onClose={() => setCustomMealTarget(null)}
          onBack={
            customMealTarget?.mode === 'add' && customMealTarget.replacingName
              ? () => {
                  setCustomMealTarget(null);
                  setSubstitutionModalOpen(true);
                }
              : undefined
          }
          dayLabel={
            customMealTarget?.mode === 'edit'
              ? customMealTarget.assignment.plannedDay
              : customMealTarget?.plannedDay ?? ''
          }
          mode={customMealTarget?.mode ?? 'add'}
          replacingName={customMealTarget?.mode === 'add' ? customMealTarget.replacingName : undefined}
          initial={
            customMealTarget?.mode === 'edit'
              ? customMealTarget.assignment.customMeal
              : undefined
          }
          savedMeals={savedPenciledMeals ?? undefined}
          mealType={
            customMealTarget?.mode === 'edit'
              ? getAssignmentMealType(customMealTarget.assignment)
              : customMealTarget?.mealType ?? 'dinner'
          }
          saving={savingCustomMeal}
          onSubmit={handleSubmitCustomMeal}
        />

        {/* Meal Plan Preferences Drawer */}
        {mealPlan && (
          <MealPlanPreferencesDrawer
            opened={preferencesModalOpen}
            onClose={() => setPreferencesModalOpen(false)}
            preferences={mealPlan.preferences}
            profileSnapshot={mealPlan.profileSnapshot}
            totalDays={(() => {
              const [startYear, startMonth, startDay] = mealPlan.startDate.split('-').map(Number);
              const [endYear, endMonth, endDay] = mealPlan.endDate.split('-').map(Number);
              const start = new Date(startYear, startMonth - 1, startDay);
              const end = new Date(endYear, endMonth - 1, endDay);
              return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
            })()}
            dateRange={[mealPlan.startDate, mealPlan.endDate]}
          />
        )}

        {/* Kroger Location Selection Modal */}
        <KrogerLocationModal
          opened={locationModalOpen}
          onClose={() => setLocationModalOpen(false)}
          onLocationSelected={handleLocationSelected}
          title="Select your Kroger store"
          description="Choose your preferred Kroger location to add ingredients to your cart."
        />

        {/* Shopping Customization Drawer */}
        <ShoppingCustomizationDrawer
          opened={additionalItemsDrawerOpen}
          onClose={() => setAdditionalItemsDrawerOpen(false)}
          items={mealPlan?.additionalItems || []}
          onSave={handleSaveAdditionalItems}
          loading={savingAdditionalItems}
          isKrogerConnected={isKrogerConnected()}
          productPreferences={userProfile?.productPreferences}
          initialProductMatchingInstructions={mealPlan?.productMatchingInstructions || ''}
          initialProductPreferenceOverrides={mealPlan?.productPreferenceOverrides || {}}
          groceryList={groceryList}
        />

        {/* Shopping List Drawer - the full grocery list (bottom sheet on mobile, side panel on desktop) */}
        <ShoppingListDrawer
          opened={shoppingListDrawerOpen}
          onClose={() => setShoppingListDrawerOpen(false)}
          mealPlan={mealPlan}
          planId={effectivePlanId}
          groceryList={groceryList}
          setGroceryList={setGroceryList}
          activeShoppingTab={activeShoppingTab}
          updateActiveShoppingTab={updateActiveShoppingTab}
          getCategorizedItems={getCategorizedItems}
          getCategoryDisplayName={getCategoryDisplayName}
          handleToggleGroceryItem={handleToggleGroceryItem}
          handlePrintShoppingList={handlePrintShoppingList}
          groceryMutationSeqRef={groceryMutationSeqRef}
          pantryItems={pantryItems}
          fetchPantryItems={fetchPantryItems}
          isOwner={canModifyPlan}
          user={user}
          navigate={navigate}
          isKrogerConnected={isKrogerConnected}
          expandedPreferenceItems={expandedPreferenceItems}
          handleToggleProductPreference={handleToggleProductPreference}
          handleSaveProductPreference={handleSaveProductPreference}
        />

        {/* Feedback Modal */}
        {feedbackContext && (
          <ContextualFeedbackModal
            opened={feedbackModalOpen}
            onClose={() => {
              setFeedbackModalOpen(false);
              setTimeout(() => setFeedbackContext(null), 300);
            }}
            context={feedbackContext}
          />
        )}

        {/* Recipe Rating Modal */}
        {ratingAssignment && (
          <RecipeRatingModal
            opened={ratingModalOpen}
            onClose={() => {
              setRatingModalOpen(false);
              setTimeout(() => setRatingAssignment(null), 300);
            }}
            recipe={getAssignmentRecipe(ratingAssignment)!}
            userDietaryRestrictions={userProfile?.dietaryRestrictions}
            kids={user?.familyProfile?.kids ?? 0}
            onSubmit={handleRateDownSubmit}
          />
        )}

        {/* Nutrition Drawer */}
        {selectedRecipeForNutrition?.nutrition && (
          <NutritionDrawer
            opened={nutritionDrawerOpened}
            onClose={() => {
              setNutritionDrawerOpened(false);
              setTimeout(() => setSelectedRecipeForNutrition(null), 300);
            }}
            nutrition={selectedRecipeForNutrition.nutrition}
            recipeName={selectedRecipeForNutrition.name}
            recipeId={selectedRecipeForNutrition.id}
            ingredientCount={selectedRecipeForNutrition.ingredients?.length}
          />
        )}

      </Stack>
    </MobileOptimized>
  );
};
